/* ============================================================================
   ADA fix - WCAG 2.1 AA runtime accessibility patches
   Dope Slimes LLC - added 16-09-2026, consolidated 18-09-2026
   Loaded from layout/theme.liquid. Theme-side only.
   Nothing in this file changes what the store does for shoppers; it corrects
   names, roles, states and keyboard behaviour that assistive technology and
   keyboard users depend on, including for markup injected by apps (reviews,
   wishlist, mega menu, page builder sections, slide cart, product builder).
   Nothing here changes the visual design.
   ========================================================================= */
(function () {
  'use strict';

  /* =========================================================================
     0. Touch scrolling on a phone - WCAG 2.1.1 and plain usability
        A drawer app stops the page moving under it on a phone by refusing
        every touch move at document level for as long as it is open. That
        refusal is meant to be withdrawn when the drawer closes, and a drawer
        that closes without withdrawing it leaves the whole page unable to be
        scrolled by touch until the shopper reloads, while a mouse and a
        keyboard still work, which is why it shows only on a phone.
        Every such refusal is noted here as it is set up, keeping the very
        function that was registered, so it can be lifted while nothing is
        open and handed back unchanged the moment a drawer opens. Refusals
        set up on a piece of the page, such as a slider that moves with a
        finger, are not touched at all.
     ====================================================================== */
  var pageTouchBlocks = [];
  var rawAdd = null;
  var rawRemove = null;
  (function noteTouchBlocks() {
    if (!window.EventTarget || !window.EventTarget.prototype ||
        !window.EventTarget.prototype.addEventListener) { return; }
    rawAdd = window.EventTarget.prototype.addEventListener;
    rawRemove = window.EventTarget.prototype.removeEventListener;
    /* Only a refusal registered on the document itself is noted. A slider that
       follows a finger registers its own on the piece of the page it belongs
       to, or on the window while a drag is under way, and those are never
       touched. */
    function wholePage(target) {
      return target === document;
    }
    window.EventTarget.prototype.addEventListener = function (type, fn, opts) {
      try {
        /* Only a refusal that is set up to be able to stop the page, that is
           one registered as not passive, is noted. Anything else is left
           completely alone. */
        if (type === 'touchmove' && fn && wholePage(this) &&
            opts && typeof opts === 'object' && opts.passive === false) {
          var known = false;
          for (var i = 0; i < pageTouchBlocks.length; i++) {
            if (pageTouchBlocks[i].target === this && pageTouchBlocks[i].fn === fn) {
              known = true;
              pageTouchBlocks[i].live = true;
            }
          }
          if (!known) { pageTouchBlocks.push({ target: this, fn: fn, opts: opts, live: true }); }
        }
      } catch (noteFailed) { /* ignore */ }
      return rawAdd.call(this, type, fn, opts);
    };
    window.EventTarget.prototype.removeEventListener = function (type, fn, opts) {
      try {
        if (type === 'touchmove' && fn) {
          for (var j = pageTouchBlocks.length - 1; j >= 0; j--) {
            if (pageTouchBlocks[j].target === this && pageTouchBlocks[j].fn === fn) {
              pageTouchBlocks.splice(j, 1);
            }
          }
        }
      } catch (noteFailed) { /* ignore */ }
      return rawRemove.call(this, type, fn, opts);
    };
  })();
  /* Lift every page wide refusal while nothing is open; hand each one back,
     exactly as it was registered, as soon as something is open again. */
  var fingerDown = false;
  function holdPageTouch(hold) {
    if (!rawAdd || !rawRemove) { return; }
    /* Never lift anything in the middle of a finger movement: a slider binds
       its own handling as a drag begins. */
    if (!hold && fingerDown) { return; }
    for (var i = 0; i < pageTouchBlocks.length; i++) {
      var b = pageTouchBlocks[i];
      try {
        if (hold && !b.live) { rawAdd.call(b.target, 'touchmove', b.fn, b.opts); b.live = true; }
        else if (!hold && b.live) { rawRemove.call(b.target, 'touchmove', b.fn, b.opts); b.live = false; }
      } catch (swapFailed) { /* ignore */ }
    }
  }

  /* ---------------------------------------------------------------- utils */
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }
  function debounce(fn, wait) {
    var t = null;
    return function () { if (t) { clearTimeout(t); } t = setTimeout(fn, wait); };
  }
  function each(sel, fn, root) {
    var n = (root || document).querySelectorAll(sel);
    for (var i = 0; i < n.length; i++) { fn(n[i], i); }
  }
  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) { el.setAttribute(name, value); }
  }
  var uid = 0;
  function ensureId(el, prefix) {
    var p = prefix || 'ada';
    /* An app that clones part of the header clones the id with it, and two
       elements with one id break every aria-controls that points at it. An id
       of our own is regenerated until it is unique: an id the theme or an app
       wrote is left exactly as it is. */
    if (el.id && el.id.indexOf(p + '-') !== 0) { return el.id; }
    if (el.id && document.getElementById(el.id) === el) { return el.id; }
    do { uid++; el.id = p + '-' + uid; } while (document.getElementById(el.id) !== el);
    return el.id;
  }
  function textOf(el) {
    return (el && el.textContent ? el.textContent : '').replace(/\s+/g, ' ').trim();
  }
  /* Text a shopper or a screen reader actually gets from an element. The theme
     puts a <noscript> copy of every lazy-loaded image inside its link, and the
     quick view app puts a "..." loading label there too. */
  function visibleText(el) {
    if (!el) { return ''; }
    var c = el.cloneNode(true);
    var junk = c.querySelectorAll('noscript, script, style, .qview-button, .qview-button-wrapper, [aria-hidden="true"]');
    for (var i = 0; i < junk.length; i++) {
      if (junk[i].parentNode) { junk[i].parentNode.removeChild(junk[i]); }
    }
    var t = (c.textContent || '').replace(/\s+/g, ' ').trim();
    if (t && t !== '...') { return t; }
    var img = el.querySelector('img[alt]');
    if (img && img.getAttribute('alt').trim()) { return img.getAttribute('alt').trim(); }
    return '';
  }
  function isShown(el) {
    if (!el) { return false; }
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden') { return false; }
    var r = el.getBoundingClientRect();
    return !(r.width === 0 && r.height === 0);
  }
  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,summary,[tabindex]:not([tabindex="-1"])';
  function focusables(root) {
    var out = [];
    each(FOCUSABLE, function (el) {
      if (isShown(el) && !el.closest('[aria-hidden="true"]')) { out.push(el); }
    }, root);
    return out;
  }
  function fireMouse(el, type) {
    try { el.dispatchEvent(new MouseEvent(type, { bubbles: true })); } catch (e) { /* ignore */ }
  }
  function srOnly(tag, text) {
    var el = document.createElement(tag);
    el.className = 'ada-sr-only';
    el.setAttribute('data-ada-generated', '1');
    el.textContent = text;
    return el;
  }
  var mobileMQ = window.matchMedia ? window.matchMedia('(max-width: 798px)') : null;
  function isMobileLayout() { return mobileMQ ? mobileMQ.matches : window.innerWidth <= 798; }

  /* =========================================================================
     1. Cart links - accessible name that includes the live item count
        WCAG 4.1.2 Name, Role, Value / 2.4.4 Link Purpose
     ====================================================================== */
  function cartName(n) {
    n = parseInt(n, 10);
    if (isNaN(n) || n < 0) { n = 0; }
    return 'Cart, ' + n + (n === 1 ? ' item' : ' items');
  }
  function readCount(scope) {
    var el = scope.querySelector('.cart_count');
    if (!el) { return null; }
    var v = parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10);
    return isNaN(v) ? null : v;
  }
  function isIconOnlyCartLink(a) {
    var txt = textOf(a);
    return txt === '' || /^[0-9\s$.,]*$/.test(txt);
  }
  function labelCartLinks() {
    var pageCount = readCount(document);
    each('a.mini_cart, a.icon-cart, a.icon-bag, a[href="/cart"], a[href$="/cart"]', function (a) {
      if (!isIconOnlyCartLink(a)) { return; }
      var c = readCount(a);
      if (c === null) { c = pageCount; }
      if (c === null) { c = 0; }
      setAttr(a, 'aria-label', cartName(c));
      each('svg, i, .icon, use', function (g) { setAttr(g, 'aria-hidden', 'true'); }, a);
    });
    /* Remove links in the header mini cart and on the cart page were named
       "Cart"; each one now says which item it removes. */
    each('a.cart__remove-btn, button.cart__remove-btn', function (a) {
      var item = a.closest('[data-cart-item], .mini-cart__item, .cart__item, .cart__row, tr, li');
      var title = item ? textOf(item.querySelector('.item_title, .mini-cart__item__title, .cart__item-title, .cart_page_title, a[href*="/products/"]')) : '';
      var name = title ? 'Remove ' + title : 'Remove item';
      var cur = a.getAttribute('aria-label') || '';
      if (!cur || cur === 'Cart' || /^Remove,?\s*$/.test(cur)) { setAttr(a, 'aria-label', name); }
    });
  }

  /* =========================================================================
     2. Keyboard operation for controls the theme built out of spans
        WCAG 2.1.1 Keyboard
     ====================================================================== */
  function activateOnKey(e) {
    var el = e.target.closest('[role="button"]');
    if (!el) { return; }
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') { return; }
    if (el.tagName === 'A' && el.hasAttribute('href')) { return; }
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') { return; }
    e.preventDefault();
    el.click();
  }

  /* =========================================================================
     3. Collapsible sidebar sections
        WCAG 2.1.1 Keyboard / 4.1.2 Name, Role, Value
        Below 799px the theme turns these headings into click-to-open toggles.
        The heading text is moved into a transparent button (the standard
        accordion pattern) so the panel can be opened from the keyboard and its
        state is announced. From 799px up the panel is always open and the
        theme disables the toggle, so the button steps out of the tab order.
     ====================================================================== */
  function upgradeToggleHeadings() {
    each('h2.toggle, h3.toggle, h4.toggle, h5.toggle, h6.toggle', function (h) {
      if (h.closest('.slidecarthq, #slidecarthq')) { return; }
      var btn = h.querySelector('button.ada-toggle-btn');
      if (btn) { if (h.__adaSync) { h.__adaSync(); } return; }
      h.setAttribute('data-ada-toggle', '1');
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ada-toggle-btn';
      while (h.firstChild) { btn.appendChild(h.firstChild); }
      h.appendChild(btn);
      var panel = h.nextElementSibling;
      if (panel) { btn.setAttribute('aria-controls', ensureId(panel, 'ada-panel')); }
      var sync = function () {
        if (!isMobileLayout()) {
          /* From 799px up the theme keeps the panel open and disables the
             toggle, so the button leaves the tab order and drops the state
             and the relationship it no longer describes. Its own button role
             is left alone: a role of presentation on a button that still
             carries aria attributes is a conflict in its own right. */
          setAttr(btn, 'tabindex', '-1');
          btn.removeAttribute('role');
          btn.removeAttribute('aria-expanded');
          btn.removeAttribute('aria-controls');
          return;
        }
        btn.removeAttribute('tabindex');
        btn.removeAttribute('role');
        if (panel) { setAttr(btn, 'aria-controls', ensureId(panel, 'ada-panel')); }
        var open = true;
        if (panel) {
          var s = window.getComputedStyle(panel);
          open = !(s.display === 'none' || s.visibility === 'hidden');
        }
        setAttr(btn, 'aria-expanded', open ? 'true' : 'false');
      };
      sync();
      h.__adaSync = sync;
      h.addEventListener('click', function () { window.setTimeout(sync, 60); window.setTimeout(sync, 450); });
      window.addEventListener('resize', debounce(sync, 150));
    });
  }

  /* =========================================================================
     4. Header dropdowns and the mega menu
        WCAG 4.1.2 Name, Role, Value / 2.1.2 No Keyboard Trap / 2.4.3 Focus Order
     ====================================================================== */
  var lastToggle = null;
  /* two toggles can share one data-dropdown value when an app clones the header;
     give each toggle the panel that sits nearest to it so ids stay unique */
  function panelForToggle(toggle, rel) {
    var sel = '[data-dropdown="' + (window.CSS && CSS.escape ? CSS.escape(rel) : rel) + '"]';
    var all = [].slice.call(document.querySelectorAll(sel));
    if (all.length < 2) { return all[0] || null; }
    var best = null, bestDepth = -1;
    for (var i = 0; i < all.length; i++) {
      var n = toggle.parentElement, depth = 0;
      while (n && !n.contains(all[i])) { n = n.parentElement; depth++; }
      if (n && (bestDepth < 0 || depth < bestDepth)) { bestDepth = depth; best = all[i]; }
    }
    return best || all[0];
  }
  function isOpen(panel) {
    if (!panel) { return false; }
    var s = window.getComputedStyle(panel);
    return !(s.display === 'none' || s.visibility === 'hidden');
  }
  function wireDropdowns() {
    each('[data-dropdown-rel]', function (toggle) {
      var rel = toggle.getAttribute('data-dropdown-rel');
      var panel = panelForToggle(toggle, rel);
      if (!panel) { return; }
      setAttr(toggle, 'aria-haspopup', 'true');
      setAttr(toggle, 'aria-controls', ensureId(panel, 'ada-dropdown'));
      var sync = function () {
        setAttr(toggle, 'aria-expanded', isOpen(panel) ? 'true' : 'false');
      };
      sync();
      if (toggle.getAttribute('data-ada-dd') === '1') { return; }
      toggle.setAttribute('data-ada-dd', '1');
      toggle.addEventListener('click', function () {
        lastToggle = toggle;
        window.setTimeout(sync, 80);
        window.setTimeout(sync, 450);
        /* Opened from the keyboard, the panel has to be given focus: the logo,
           the search icon and the cart link sit between the toggle and the
           panel in the source order. */
        var byKeyboard = true;
        try { byKeyboard = toggle.matches(':focus-visible'); } catch (noFocusVisible) { /* ignore */ }
        if (Date.now() - lastPointerAt < 800) { byKeyboard = false; }
        if (!byKeyboard) { return; }
        window.setTimeout(function () {
          if (!isOpen(panel)) { return; }
          if (panel.contains(document.activeElement)) { return; }
          var first = focusables(panel)[0];
          if (first) { try { first.focus(); } catch (cannotFocus) { /* ignore */ } }
        }, 160);
      });
      toggle.addEventListener('mouseenter', function () { window.setTimeout(sync, 120); });
      toggle.addEventListener('focus', function () { lastToggle = toggle; });
      try {
        new MutationObserver(sync).observe(panel, { attributes: true, attributeFilter: ['style', 'class'] });
        new MutationObserver(sync).observe(toggle, { attributes: true, attributeFilter: ['class'] });
      } catch (e) { /* ignore */ }
    });
  }
  function closeAllDropdowns(open) {
    if (window.jQuery) {
      window.jQuery('body').removeClass('is-active').removeClass('blocked-scroll');
      window.jQuery('.dropdown_link').removeClass('active_link');
      window.jQuery('.dropdown_container').hide();
      window.jQuery('.mobile_nav').find('div').removeClass('open');
    } else if (open) {
      open.style.display = 'none';
    }
  }
  /* Escape closes any open header dropdown and returns focus to its toggle. */
  function escapeClosesDropdowns(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') { return; }
    var open = null;
    each('[data-dropdown]', function (p) { if (!open && isOpen(p)) { open = p; } });
    if (!open) { return; }
    var rel = open.getAttribute('data-dropdown');
    var toggle = document.querySelector('[data-dropdown-rel="' + rel + '"]');
    closeAllDropdowns(open);
    if (toggle) {
      toggle.focus();
      setAttr(toggle, 'aria-expanded', 'false');
      var refocus = function () {
        var now = document.activeElement;
        if (!now || now === document.body || now === document.documentElement) { try { toggle.focus(); } catch (cannotFocus) { /* ignore */ } }
      };
      window.setTimeout(refocus, 0);
      window.setTimeout(refocus, 120);
    }
    e.preventDefault();
  }
  /* The mega menu app cancels the Tab key inside its own panel, which is a
     keyboard trap. This listener runs in the capture phase at the document,
     before the event can reach the app, and stops it there. */
  function letTabOutOfMegaMenu(e) {
    if (e.key !== 'Tab') { return; }
    var t = e.target;
    if (!t || !t.closest) { return; }
    if (!t.closest('.main_nav')) { return; }
    e.stopPropagation();
  }
  /* A header dropdown is a menu, not a modal dialog: Tab walks out of the
     panel normally, and the panel closes behind it, exactly as it does when
     the mouse moves away. */
  function closeDropdownWhenFocusLeaves(e) {
    var open = null;
    each('.dropdown_container', function (p) { if (!open && isOpen(p)) { open = p; } });
    if (!open) { return; }
    var rel = open.getAttribute('data-dropdown');
    var toggle = rel ? document.querySelector('[data-dropdown-rel="' + rel + '"]') : null;
    var to = e.relatedTarget;
    /* Only a Tab key press closes the panel. A tap, a scroll or the page
       losing focus moves focus to nothing, and on phones that happens while
       the shopper is still using the menu, so the panel is left open. */
    if (!to || Date.now() - lastTabAt > 800 || Date.now() - lastPointerAt < 800) { return; }
    if (open.contains(to) || (toggle && (toggle === to || toggle.contains(to)))) { return; }
    closeAllDropdowns(open);
    if (toggle) { setAttr(toggle, 'aria-expanded', 'false'); }
  }

  /* =========================================================================
     4b. The app-rendered mega menu (desktop)
         WCAG 2.1.1 Keyboard
     ====================================================================== */
  function wireAppMegaMenu() {
    each('li.tmenu_item--root[class*="tmenu_item_has_child"] > a.tmenu_item_link', function (link) {
      if (link.getAttribute('data-ada-mm') === '1') { return; }
      link.setAttribute('data-ada-mm', '1');
      var li = link.parentElement;
      var send = function (types) {
        for (var i = 0; i < types.length; i++) {
          fireMouse(link, types[i]);
          fireMouse(li, types[i]);
        }
      };
      var getSub = function () { return li.querySelector('[class*="tmenu_submenu"]'); };
      var openedByKey = false;
      var syncExpanded = function () {
        setAttr(link, 'aria-expanded', li.className.indexOf('tmenu_item_active') >= 0 ? 'true' : 'false');
      };
      var open = function () {
        openedByKey = true;
        if (li.className.indexOf('tmenu_item_active') < 0) { li.className = li.className + ' tmenu_item_active'; }
        var sub = getSub();
        if (sub) {
          sub.style.setProperty('visibility', 'visible', 'important');
          sub.style.setProperty('opacity', '1', 'important');
        }
        send(['pointerenter', 'mouseenter', 'mouseover']);
        syncExpanded();
      };
      var close = function () {
        openedByKey = false;
        li.className = li.className.replace(/\s*tmenu_item_active\b/g, '');
        var sub = getSub();
        if (sub) {
          sub.style.removeProperty('visibility');
          sub.style.removeProperty('opacity');
        }
        send(['pointerleave', 'mouseleave', 'mouseout']);
        syncExpanded();
      };
      var holdOpen = function () {
        if (li.__adaEscaped || li.getAttribute('data-ada-escaped')) {
          /* Only write when the class is actually present. Writing the same
             value again would fire this observer again, round and round. */
          if (/\btmenu_item_active\b/.test(li.className)) {
            li.className = li.className.replace(/\s*tmenu_item_active\b/g, '');
          }
          return;
        }
        if (!li.contains(document.activeElement)) { syncExpanded(); return; }
        /* Focus resting on the top level item on its own does not open the
           panel: a keyboard user opens it deliberately with Enter, Space or
           the Down arrow, the way the W3C authoring practices describe. The
           panel is only held open once it has been opened. */
        if (document.activeElement === link && !openedByKey &&
            li.className.indexOf('tmenu_item_active') < 0) { syncExpanded(); return; }
        if (li.className.indexOf('tmenu_item_active') < 0) { li.className = li.className + ' tmenu_item_active'; }
        syncExpanded();
      };
      li.addEventListener('focusin', holdOpen);
      try {
        new MutationObserver(holdOpen).observe(li, { attributes: true, attributeFilter: ['class'] });
      } catch (noObserver) { /* ignore */ }
      setAttr(link, 'aria-haspopup', 'true');
      syncExpanded();
      link.addEventListener('focus', function () {
        if (li.__adaEscaped) { li.__adaEscaped = false; li.removeAttribute('data-ada-escaped'); }
        syncExpanded();
      });
      /* Enter, Space or the Down arrow opens the panel; Enter again follows
         the top level link, so the shopper can still reach its own page.
         Nothing here changes what a mouse does: hover still opens it. */
      link.addEventListener('keydown', function (e) {
        var key = e.key;
        var isOpenNow = li.className.indexOf('tmenu_item_active') >= 0;
        if (key === 'ArrowDown' || key === 'Down') {
          li.__adaEscaped = false;
          li.removeAttribute('data-ada-escaped');
          open();
          e.preventDefault();
          window.setTimeout(function () {
            var sub = getSub();
            var first = sub ? focusables(sub)[0] : null;
            if (first) { try { first.focus(); } catch (cannotFocus) { /* ignore */ } }
          }, 80);
          return;
        }
        if (key === ' ' || key === 'Spacebar' || key === 'Space') {
          li.__adaEscaped = false;
          li.removeAttribute('data-ada-escaped');
          if (isOpenNow) { close(); } else { open(); }
          e.preventDefault();
          return;
        }
        if (key === 'Enter' && !isOpenNow) {
          li.__adaEscaped = false;
          li.removeAttribute('data-ada-escaped');
          open();
          e.preventDefault();
        }
      });
      li.addEventListener('focusout', function (e) {
        if (e.relatedTarget && li.contains(e.relatedTarget)) { return; }
        /* Taps and clicks are left to the menu app itself. */
        if (Date.now() - lastPointerAt < 800) { return; }
        li.removeAttribute('data-ada-escaped');
        close();
      });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          li.__adaEscaped = true;
          li.setAttribute('data-ada-escaped', '1');
          close();
          link.focus();
          window.setTimeout(function () { close(); li.__adaEscaped = false; }, 120);
          e.preventDefault();
          e.stopPropagation();
        }
      });
    });
  }
  /* Mega menu tab panels: the tab that owns the focused element is activated,
     so whatever holds focus is always the panel being shown. */
  function megaTabActivate(control, index) {
    var li = control.children[index];
    if (!li || li.className.indexOf('tmenu_submenu_tab_active') >= 0) { return; }
    var content = li.parentElement && li.parentElement.parentElement
      ? li.parentElement.parentElement.querySelector('.tmenu_submenu_tab_content') : null;
    var panels = [];
    if (content) {
      for (var p = 0; p < content.children.length; p++) {
        if (content.children[p].className.indexOf('tmenu_submenu_tab_item') >= 0) { panels.push(content.children[p]); }
      }
    }
    for (var c = 0; c < control.children.length; c++) {
      var other = control.children[c];
      other.className = other.className.replace(/\s*tmenu_submenu_tab_active\b/g, '');
      if (panels[c]) { panels[c].className = panels[c].className.replace(/\s*tmenu_submenu_tab_active\b/g, ''); }
    }
    li.className = li.className + ' tmenu_submenu_tab_active';
    for (var q = 0; q < panels.length; q++) {
      if (q === index) { continue; }
      panels[q].style.removeProperty('visibility'); panels[q].style.removeProperty('opacity');
    }
    if (panels[index]) {
      panels[index].className = panels[index].className + ' tmenu_submenu_tab_active';
      panels[index].style.setProperty('visibility', 'visible', 'important');
      panels[index].style.setProperty('opacity', '1', 'important');
    }
    var targets = [li, li.querySelector('a')];
    var types = ['pointerenter', 'mouseenter', 'mouseover'];
    for (var t = 0; t < targets.length; t++) {
      if (!targets[t]) { continue; }
      for (var k = 0; k < types.length; k++) { fireMouse(targets[t], types[k]); }
    }
  }
  function wireMegaMenuTabs() {
    each('.tmenu_submenu_tab_control', function (control) {
      var sub = control.parentElement;
      if (!sub) { return; }
      var content = sub.querySelector('.tmenu_submenu_tab_content');
      if (!content) { return; }
      var panels = [];
      for (var p = 0; p < content.children.length; p++) {
        if (content.children[p].className.indexOf('tmenu_submenu_tab_item') >= 0) { panels.push(content.children[p]); }
      }
      for (var i = 0; i < control.children.length; i++) {
        (function (index) {
          var li = control.children[index];
          var link = li.querySelector('a');
          if (link) {
            link.setAttribute('tabindex', '0');
            if (link.getAttribute('data-ada-ti') !== '1') {
              link.setAttribute('data-ada-ti', '1');
              (function (lk) {
                try {
                  new MutationObserver(function () {
                    if (lk.getAttribute('tabindex') !== '0') { lk.setAttribute('tabindex', '0'); }
                  }).observe(lk, { attributes: true, attributeFilter: ['tabindex'] });
                } catch (noObserver) { /* ignore */ }
              }(link));
            }
            if (link.getAttribute('data-ada-mt') !== '1') {
              link.setAttribute('data-ada-mt', '1');
              link.addEventListener('focus', function () { megaTabActivate(control, index); });
            }
          }
          var panel = panels[index];
          if (panel && panel.getAttribute('data-ada-mt') !== '1') {
            panel.setAttribute('data-ada-mt', '1');
            var holdTab = function () {
              if (!panel.contains(document.activeElement)) { return; }
              megaTabActivate(control, index);
            };
            panel.addEventListener('focusin', holdTab);
            try {
              new MutationObserver(holdTab).observe(panel, { attributes: true, attributeFilter: ['class'] });
            } catch (noObserver) { /* ignore */ }
          }
        }(i));
      }
      var anyActive = false;
      for (var q = 0; q < panels.length; q++) {
        if (panels[q].className.indexOf('tmenu_submenu_tab_active') >= 0) { anyActive = true; break; }
      }
      if (!anyActive && panels.length) {
        var inside = control.contains(document.activeElement);
        for (var z = 0; z < panels.length && !inside; z++) {
          if (panels[z].contains(document.activeElement)) { inside = true; }
        }
        if (!inside) { megaTabActivate(control, 0); }
      }
    });
  }
  /* Focus must never land nowhere: if the menu app rebuilds the element that
     holds focus, focus is put back on it or on whatever replaced it. */
  var HEADER_SEL = '.main_nav, .dropdown_container, .tmenu_menu, .mobile_nav';
  var lastHeaderFocus = null, lastTabAt = 0, lastPointerAt = 0;
  function noteTab(e) { if (e.key === 'Tab') { lastTabAt = Date.now(); } }
  function notePointer() { lastPointerAt = Date.now(); }
  function rememberHeaderFocus(e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest(HEADER_SEL)) { return; }
    lastHeaderFocus = { el: t, href: t.getAttribute('href'), text: textOf(t).slice(0, 60) };
  }
  function restoreLostHeaderFocus() {
    if (!lastHeaderFocus) { return; }
    var now = Date.now();
    if (now - lastTabAt > 600) { return; }
    if (now - lastPointerAt < 600) { return; }
    var a = document.activeElement;
    if (a && a !== document.body && a !== document.documentElement) { return; }
    var el = lastHeaderFocus.el;
    if (!document.contains(el)) {
      el = null;
      var cands = document.querySelectorAll('.main_nav a[href], .dropdown_container a[href], .tmenu_menu a[href]');
      for (var i = 0; i < cands.length; i++) {
        if (cands[i].getAttribute('href') === lastHeaderFocus.href && textOf(cands[i]).slice(0, 60) === lastHeaderFocus.text) { el = cands[i]; break; }
      }
    }
    if (!el) { return; }
    try { el.focus(); } catch (cannotFocus) { /* ignore */ }
  }

  /* =========================================================================
     5. Overlay panels and drawers presented as dialogs
        WCAG 4.1.2 / 2.1.2 No Keyboard Trap / 2.4.3 Focus Order
        The slide cart and the pop-ups are announced as dialogs, focus moves in
        when they open and back to the opener when they close, Tab stays
        inside, Escape closes. While closed they are inert, so nothing inside
        them can be reached or announced.
     ====================================================================== */
  var DIALOGS = [
    { sel: '.slidecarthq', label: 'Shopping cart' },
    { sel: '#popup-newsletter, .popup-newsletter, .newsletter_popup', label: 'Newsletter sign up' },
    { sel: '#popup-quick-shop, .quick-shop-popup', label: 'Quick view' },
    { sel: '#popup-size-chart, .size-chart-popup', label: 'Size chart' }
  ];
  var dialogReturn = null;
  function dialogVisible(el) {
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || parseFloat(s.opacity) === 0) { return false; }
    /* The slide cart marks its own open state with a class. Honour it, because
       a transform can still be part way through when this runs. */
    if (el.classList && el.classList.contains('slidecarthq')) { return el.classList.contains('open'); }
    var r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) { return false; }
    var vw = window.innerWidth || 0, vh = window.innerHeight || 0;
    var onW = Math.min(r.right, vw) - Math.max(r.left, 0);
    var onH = Math.min(r.bottom, vh) - Math.max(r.top, 0);
    if (onW < 24 || onH < 24) { return false; }
    if (onW < r.width * 0.25 && onW < 120) { return false; }
    return true;
  }
  function setInert(el, inert) {
    if (inert && el.contains(document.activeElement)) { return; }
    if ('inert' in el) { el.inert = !!inert; }
    if (inert) {
      setAttr(el, 'aria-hidden', 'true');
      each(FOCUSABLE, function (f) {
        if (!f.hasAttribute('data-ada-ti')) {
          f.setAttribute('data-ada-ti', f.getAttribute('tabindex') === null ? '' : f.getAttribute('tabindex'));
        }
        f.setAttribute('tabindex', '-1');
      }, el);
    } else {
      el.removeAttribute('aria-hidden');
      each('[data-ada-ti]', function (f) {
        var v = f.getAttribute('data-ada-ti');
        if (v === '') { f.removeAttribute('tabindex'); } else { f.setAttribute('tabindex', v); }
        f.removeAttribute('data-ada-ti');
      }, el);
    }
  }
  function wireDialogs() {
    DIALOGS.forEach(function (d) {
      each(d.sel, function (el) {
        setAttr(el, 'role', 'dialog');
        setAttr(el, 'aria-modal', 'true');
        if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
          setAttr(el, 'aria-label', d.label);
        }
        /* An app drawer may ship its own <header> and <footer>, which would
           otherwise be exposed as page landmarks. */
        each('header, footer', function (h) { setAttr(h, 'role', 'presentation'); }, el);
        setInert(el, !dialogVisible(el));
        if (el.getAttribute('data-ada-dialog') === '1') { return; }
        el.setAttribute('data-ada-dialog', '1');
        var check = function () {
          var vis = dialogVisible(el);
          setInert(el, !vis);
          if (vis && !el.__adaOpen) {
            el.__adaOpen = true;
            dialogReturn = document.activeElement;
            window.setTimeout(function () {
              var list = focusables(el);
              if (list.length && !el.contains(document.activeElement)) { list[0].focus(); }
            }, 120);
          } else if (!vis && el.__adaOpen) {
            el.__adaOpen = false;
            if (dialogReturn && document.contains(dialogReturn)) {
              try { dialogReturn.focus(); } catch (e) { /* ignore */ }
            }
            dialogReturn = null;
          }
        };
        var checkLater = function () { check(); window.setTimeout(check, 350); window.setTimeout(check, 900); window.setTimeout(check, 1800); };
        el.__adaCheck = checkLater;
        el.addEventListener('transitionend', check);
        try {
          new MutationObserver(checkLater).observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
        } catch (e) { /* ignore */ }
      });
    });
    /* the slide cart backdrop is a click target only; it must not be a tab stop */
    each('.slidecarthq-overlay', function (o) {
      setAttr(o, 'tabindex', '-1');
      setAttr(o, 'aria-hidden', 'true');
    });
  }
  /* =========================================================================
     5b. Full-screen layers left behind by an app - WCAG 2.1.1 Keyboard,
         2.4.3 Focus Order, and plain usability
         The slide cart and its upsell panel each lay a transparent sheet over
         the whole window while they are open. When the drawer is closed that
         sheet is faded out, but it is left in place and still takes taps and
         clicks, which leaves the page unable to be scrolled or touched on a
         phone. While no dialog is open, any full-window sheet that cannot be
         seen is made to ignore input; it is handed straight back the moment a
         dialog opens or the sheet becomes visible again.
     ====================================================================== */
  /* Is a panel that covers the page, such as the phone menu, on screen right
     now? This is measured from what is actually drawn, because these panels
     are hidden by the stylesheet rather than by a style of their own. */
  function coveringPanelOpen() {
    if (document.querySelector('.mobile_nav[aria-expanded="true"]')) { return true; }
    var open = false;
    each('.dropdown_container, .dmenu_nav, #mobile_menu, .mobile-mega-menu', function (el) {
      if (open) { return; }
      var cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') < 0.05) { return; }
      var r = el.getBoundingClientRect();
      if (r.width > 60 && r.height > 60 && r.bottom > 0 && r.top < window.innerHeight) { open = true; }
    });
    return open;
  }
  function releaseStuckOverlays() {
    var dialogOpen = !!openDialog();
    each('[class*="overlay"], [class*="backdrop"], [class*="modal-bg"]', function (el) {
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'absolute') { return; }
      var r = el.getBoundingClientRect();
      var covers = r.width >= window.innerWidth * 0.6 && r.height >= window.innerHeight * 0.5;
      if (!covers) { return; }
      var unseen = cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') < 0.05;
      var parked = el.getAttribute('data-ada-overlay-off') === '1';
      if (!dialogOpen && unseen && !parked) {
        el.setAttribute('data-ada-overlay-off', '1');
        el.__adaPe = el.style.getPropertyValue('pointer-events');
        el.style.setProperty('pointer-events', 'none', 'important');
      } else if (parked && (dialogOpen || !unseen)) {
        el.removeAttribute('data-ada-overlay-off');
        el.style.removeProperty('pointer-events');
        if (el.__adaPe) { el.style.setProperty('pointer-events', el.__adaPe); }
      }
    });
    /* A drawer that closes without tidying up can also leave the page itself
       locked against scrolling. With nothing open, that lock is lifted. */
    var anythingOpen = dialogOpen || coveringPanelOpen();
    holdPageTouch(anythingOpen);
    if (!anythingOpen) {
      var b = document.body;
      var h = document.documentElement;
      [b, h].forEach(function (node) {
        if (!node) { return; }
        if (node.style.overflow === 'hidden' || node.style.overflowY === 'hidden') {
          node.style.removeProperty('overflow');
          node.style.removeProperty('overflow-y');
        }
        if (node.style.touchAction === 'none') { node.style.removeProperty('touch-action'); }
        if (node.style.position === 'fixed') {
          var top = parseInt(node.style.top || '0', 10);
          node.style.removeProperty('position');
          node.style.removeProperty('top');
          node.style.removeProperty('width');
          node.style.removeProperty('height');
          if (top) { window.scrollTo(0, Math.abs(top)); }
        }
      });
      ['blocked-scroll', 'no-scroll', 'noscroll', 'scroll-lock', 'overflow-hidden',
       'cart-open', 'slidecart-open', 'modal-open', 'drawer-open'].forEach(function (name) {
        if (b && b.classList.contains(name)) { b.classList.remove(name); }
        if (h && h.classList.contains(name)) { h.classList.remove(name); }
      });
    }
  }
  function recheckDialogs() {
    DIALOGS.forEach(function (d) { each(d.sel, function (el) { if (el.__adaCheck) { el.__adaCheck(); } }); });
    try { releaseStuckOverlays(); } catch (e) { /* ignore */ }
  }
  function openDialog() {
    var found = null;
    DIALOGS.forEach(function (d) {
      each(d.sel, function (el) { if (!found && dialogVisible(el)) { found = el; } });
    });
    return found;
  }
  function dialogKeys(e) {
    var dlg = openDialog();
    if (!dlg) { return; }
    if (e.key === 'Escape' || e.key === 'Esc') {
      var closer = dlg.querySelector('button[class*="close"], .slidecarthq-close, .close, [aria-label*="lose"]');
      if (closer) { closer.click(); }
      else if (window.jQuery) { window.jQuery(dlg).hide(); }
      e.preventDefault();
      return;
    }
    if (e.key === 'Tab') {
      var list = focusables(dlg);
      if (!list.length) { return; }
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!dlg.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }
  }

  /* =========================================================================
     6. Status messages for cart changes - WCAG 4.1.3
     ====================================================================== */
  var liveRegion = null;
  function ensureLiveRegion() {
    if (liveRegion && document.contains(liveRegion)) { return liveRegion; }
    liveRegion = document.createElement('div');
    liveRegion.className = 'ada-sr-only';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('data-ada-generated', '1');
    document.body.appendChild(liveRegion);
    return liveRegion;
  }
  var lastAnnounced = null;
  function announceCart() {
    var c = readCount(document);
    if (c === null) { return; }
    if (lastAnnounced === null) { lastAnnounced = c; return; }
    if (c === lastAnnounced) { return; }
    lastAnnounced = c;
    ensureLiveRegion().textContent = 'Cart updated. ' + c + (c === 1 ? ' item' : ' items') + ' in your cart.';
  }

  /* =========================================================================
     7. Page builder and review widget colours after a re-publish
        WCAG 1.4.3 Contrast (Minimum)
        The standing colour corrections live in ada-fixes.css. The page
        builder regenerates its class names every time a page is re-published,
        so this is the safety net for the areas those class names cover.
        The rule it follows is the same one used everywhere else in this fix:
        light text on a coloured band keeps its colour and the band is
        deepened in the same hue until the text passes, so headings such as
        "Buy Slime At Low Prices" stay white exactly as they were. Only text
        that is already darker than the surface behind it is darkened further.
        Text over a photograph or a gradient is never touched.
     ====================================================================== */
  function fixRepublishedPageBuilderColours(root) {
    var scope = root || document;
    if (!document.getElementById('ada-runtime-colours')) {
      var adaStyle = document.createElement('style');
      adaStyle.id = 'ada-runtime-colours';
      adaStyle.textContent = '[data-ada-c="link"][data-ada-c][data-ada-c][data-ada-c][data-ada-c][data-ada-c]{color:#5170bf !important}' +
        '[data-ada-c="muted"][data-ada-c][data-ada-c][data-ada-c][data-ada-c][data-ada-c]{color:#767575 !important}' +
        '.__pf.__pf.__pf [data-ada-c="link"]{color:#5170bf !important}' +
        '.__pf.__pf.__pf [data-ada-c="muted"]{color:#767575 !important}';
      (document.head || document.documentElement).appendChild(adaStyle);
    }
    function toRgb(c) {
      var m = String(c).match(/-?[\d.]+/g);
      if (!m || m.length < 3) { return null; }
      return { r: +m[0], g: +m[1], b: +m[2], a: m.length > 3 ? +m[3] : 1 };
    }
    function relLum(v) {
      var p = [v.r, v.g, v.b].map(function (x) {
        x = x / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2];
    }
    function ratioOf(a, b) {
      var la = relLum(a), lb = relLum(b);
      var hi = la > lb ? la : lb, lo = la > lb ? lb : la;
      return (hi + 0.05) / (lo + 0.05);
    }
    function hasOwnText(el) {
      for (var i = 0; i < el.childNodes.length; i++) {
        var n = el.childNodes[i];
        if (n.nodeType === 3 && n.textContent.trim()) { return true; }
      }
      return false;
    }
    function two(n) {
      var out = Math.max(0, Math.min(255, Math.round(n))).toString(16);
      return out.length < 2 ? '0' + out : out;
    }
    function hex(c) { return '#' + two(c.r) + two(c.g) + two(c.b); }
    function neededRatio(el) {
      var cs = window.getComputedStyle(el);
      var px = parseFloat(cs.fontSize) || 16;
      var wt = parseInt(cs.fontWeight, 10) || 400;
      if (px >= 24 || (px >= 18.66 && wt >= 700)) { return 3; }
      return 4.5;
    }
    function overPicture(el) {
      var cs = window.getComputedStyle(el);
      if (cs.textShadow && cs.textShadow !== 'none') { return true; }
      var n = el, k = 0;
      while (n && n.nodeType === 1 && k < 4) {
        var c = window.getComputedStyle(n);
        if (c.position === 'absolute' || c.position === 'fixed') {
          var host = n.offsetParent || n.parentElement;
          if (host && host.querySelector('img, picture, video')) { return true; }
        }
        n = n.parentElement; k++;
      }
      return false;
    }
    /* The page builder leaves an empty url() placeholder on the wrappers it
       lazy-loads backgrounds into. That is not a picture, so it must not stop
       the walk that works out what is behind a piece of text. */
    function paintsPicture(cs) {
      var bi = cs.backgroundImage;
      if (!bi || bi === 'none') { return false; }
      if (/^url\(\s*(["']\s*["']\s*)?\)$/.test(bi.trim())) { return false; }
      return true;
    }
    /* Flatten a stack of part-transparent layers over an opaque base. */
    function flatten(layers, base) {
      var out = { r: base.r, g: base.g, b: base.b, a: 1 };
      for (var i = layers.length - 1; i >= 0; i--) {
        var l = layers[i];
        out = {
          r: l.r * l.a + out.r * (1 - l.a),
          g: l.g * l.a + out.g * (1 - l.a),
          b: l.b * l.a + out.b * (1 - l.a),
          a: 1
        };
      }
      return out;
    }
    /* What is actually painted behind this text: the nearest ancestor that
       paints an opaque colour, with any part-transparent layers above it
       blended in, plus the element that paints the opaque colour so the band
       itself can be corrected. Returns null only when a background image or
       gradient means the colour cannot be known. */
    function panelBehind(el) {
      var n = el, layers = [];
      while (n && n.nodeType === 1) {
        var cs = window.getComputedStyle(n);
        if (paintsPicture(cs)) { return null; }
        var c = toRgb(cs.backgroundColor);
        if (c && c.a > 0) {
          if (c.a >= 1) { return { node: n, c: flatten(layers, c), layers: layers }; }
          layers.push(c);
        }
        n = n.parentElement;
      }
      return { node: null, c: flatten(layers, { r: 255, g: 255, b: 255, a: 1 }), layers: layers };
    }
    /* Deepen a coloured band in its own hue until the light text on it
       passes. The band's original colour is recorded once so repeated runs
       and several pieces of text on one band settle on a single value. */
    function deepenPanel(p, textV, want) {
      if (!p.node) { return false; }
      /* Only a band that is already a solid colour of its own is deepened.
         The page background and any near-white surface are left alone, so a
         correction is never applied by repainting a large light area. */
      if (p.node === document.body || p.node === document.documentElement) { return false; }
      if (p.node.hasAttribute('data-ada-brand')) { return false; }
      if (relLum(p.c) > 0.5) { return false; }
      var saved = p.node.getAttribute('data-ada-bg0');
      var v0 = saved ? toRgb('rgb(' + saved + ')') : p.c;
      if (!v0) { return false; }
      if (!saved) {
        p.node.setAttribute('data-ada-bg0', Math.round(p.c.r) + ',' + Math.round(p.c.g) + ',' + Math.round(p.c.b));
      }
      var target = want + 0.1; /* a small margin so rounding never lands under */
      var stack = p.layers || [];
      var f = 1, c = { r: v0.r, g: v0.g, b: v0.b };
      var seen = flatten(stack, c);
      for (var i = 0; i < 120 && ratioOf(textV, seen) < target; i++) {
        f -= 0.01;
        if (f < 0) { f = 0; }
        c = { r: v0.r * f, g: v0.g * f, b: v0.b * f };
        seen = flatten(stack, c);
      }
      if (ratioOf(textV, seen) < want) { return false; }
      var prev = parseFloat(p.node.getAttribute('data-ada-bgf'));
      if (!isNaN(prev) && prev < f) { return true; }
      p.node.setAttribute('data-ada-bgf', f);
      p.node.style.setProperty('background-color', hex(c), 'important');
      return true;
    }
    /* Darken text in its own hue until it passes against what is behind it. */
    function deepenText(el, v, bg, want) {
      var f = 1, c = { r: v.r, g: v.g, b: v.b };
      for (var i = 0; i < 60 && ratioOf(c, bg) < want; i++) {
        f -= 0.02;
        if (f < 0) { f = 0; }
        c = { r: v.r * f, g: v.g * f, b: v.b * f };
      }
      if (ratioOf(c, bg) < want && ratioOf({ r: 255, g: 255, b: 255 }, bg) >= want) {
        c = { r: 255, g: 255, b: 255 };
      }
      el.style.setProperty('color', hex(c), 'important');
      el.setAttribute('data-ada-tc', '1');
    }
    var texts = scope.querySelectorAll('.__pf *, .jdgm-carousel *, .jdgm-widget *');
    for (var j = 0; j < texts.length; j++) {
      var t = texts[j];
      try {
        if (t.hasAttribute('data-ada-c')) { continue; }
        if (!hasOwnText(t)) { continue; }
        /* A colour set on an earlier pass is withdrawn if a photograph has
           since loaded behind the text, or if the surface behind the text has
           changed since, so that the colour chosen then no longer reads on it.
           A page builder band that is still loading its picture stands in with
           a colour of its own, so a piece of text judged during that moment has
           to be judged again once the band has settled. The text is then
           measured from scratch below. */
        if (t.hasAttribute('data-ada-tc')) {
          var was = panelBehind(t);
          var nowV = toRgb(window.getComputedStyle(t).color);
          if (!was || !nowV || ratioOf(nowV, was.c) < neededRatio(t)) {
            t.style.removeProperty('color');
            t.removeAttribute('data-ada-tc');
          }
        }
        var cs = getComputedStyle(t);
        if (cs.display === 'none' || cs.visibility === 'hidden') { continue; }
        var v = toRgb(cs.color);
        if (!v) { continue; }
        if (v.a < 1) { v = { r: v.r * v.a + 255 * (1 - v.a), g: v.g * v.a + 255 * (1 - v.a), b: v.b * v.a + 255 * (1 - v.a), a: 1 }; }
        if (overPicture(t)) { continue; }
        var p = panelBehind(t);
        if (!p) {
          /* Over a photograph the surface cannot be measured. Brand blue text
             there takes the corrected link blue used everywhere else. */
          if (brandRgb(cs.color)) { t.setAttribute('data-ada-c', 'link'); }
          continue;
        }
        var bg = p.c;
        var want = neededRatio(t);
        if (ratioOf(v, bg) >= want) { continue; }
        var white = { r: 255, g: 255, b: 255 };
        if (p.node && p.node.hasAttribute('data-ada-brand') && ratioOf(white, bg) >= want) {
          /* Text on the corrected blue band that does not pass is made white,
             which is how every other piece of text on these bands is set. */
          t.style.setProperty('color', '#ffffff', 'important');
          t.setAttribute('data-ada-tc', '1');
          continue;
        }
        if (relLum(v) > relLum(bg)) {
          /* Light text on a colour band. The text stays as the designer set
             it and the band carries the correction. */
          if (deepenPanel(p, v, want)) { continue; }
          deepenText(t, v, bg, want);
          continue;
        }
        var nearWhite = relLum(bg) >= 0.75;
        var bluish = v.b > v.r + 25 && v.b > v.g + 25;
        var span = Math.max(v.r, v.g, v.b) - Math.min(v.r, v.g, v.b);
        if (nearWhite && bluish) { t.setAttribute('data-ada-c', 'link'); }
        else if (nearWhite && span < 30) { t.setAttribute('data-ada-c', 'muted'); }
        else { deepenText(t, v, bg, want); }
      } catch (perElement) { /* ignore */ }
    }
  }

  /* =========================================================================
     8. Landmarks and page structure - WCAG 1.3.1 / 2.4.6
        The header section renders two header elements (one for narrow
        screens, one for wide); the section wrapper becomes the single banner.
        Pages rendered by apps (help center, product builder) have no h1.
     ====================================================================== */
  function fixLandmarks() {
    /* The skip link is the first thing in the page and sits outside every
       landmark, which leaves it as content belonging to no region. It is
       given a navigation landmark of its own, the way skip links are
       normally marked up. Nothing about it moves or looks different. */
    var skip = document.querySelector('a.ada-skip-link');
    if (skip && skip.parentElement && skip.parentElement.tagName !== 'NAV' &&
        !skip.closest('nav, header, main, footer, aside, [role="navigation"], [role="banner"], [role="main"], [role="contentinfo"]')) {
      var wrap = document.createElement('nav');
      wrap.setAttribute('aria-label', 'Skip links');
      wrap.setAttribute('data-ada-skip-nav', '1');
      skip.parentElement.insertBefore(wrap, skip);
      wrap.appendChild(skip);
    }
    /* Shopify drops an empty holder for its security badge outside every
       landmark. While it is empty it is hidden from assistive technology,
       and once the badge is drawn into it, it becomes a named region of its
       own, so no part of the page sits outside a landmark. */
    var badge = document.getElementById('shop-hcaptcha-badge-container');
    if (badge) {
      var empty = !textOf(badge) && !badge.querySelector('a, button, iframe, img, [tabindex]');
      if (empty) {
        setAttr(badge, 'aria-hidden', 'true');
        badge.removeAttribute('role');
        badge.removeAttribute('aria-label');
      } else {
        badge.removeAttribute('aria-hidden');
        setAttr(badge, 'role', 'complementary');
        setAttr(badge, 'aria-label', 'Security check');
      }
    }
    var sec = document.getElementById('shopify-section-header');
    if (sec) {
      setAttr(sec, 'role', 'banner');
      each(':scope > header, :scope > div > header', function (h) { setAttr(h, 'role', 'presentation'); }, sec);
    }
    var main = document.getElementById('main-content');
    if (main && !document.querySelector('h1') && !main.querySelector('[data-ada-h1]')) {
      var title = '';
      var og = document.querySelector('meta[property="og:title"]');
      if (og) { title = og.getAttribute('content') || ''; }
      if (!title) { title = document.title; }
      title = title.replace(/\s*[-|\u2013\u2014]\s*(Dope Slimes|Dope\u2122 Slimes).*$/i, '').trim();
      if (title) {
        var h1 = srOnly('h1', title);
        h1.setAttribute('data-ada-h1', '1');
        main.insertBefore(h1, main.firstChild);
      }
    }
  }

  /* =========================================================================
     9. Blog listing - WCAG 1.3.1 / 2.4.4
        Every article title is a level 2 heading in the outline, and each
        "View Details" link says which article it opens.
     ====================================================================== */
  function fixBlogListing() {
    if (!document.body.classList.contains('blog') && !/\/blogs\//.test(location.pathname) && !/\/pages\/blog/.test(location.pathname)) { return; }
    each('h3', function (h) {
      if (h.closest('footer, .slidecarthq, #slidecarthq, header, .sidebar')) { return; }
      var art = h.closest('.article, article, .blog-post, .blog_post, [class*="article"]');
      if (art && h.querySelector('a[href*="/blogs/"]')) { setAttr(h, 'aria-level', '2'); }
    });
    each('a', function (a) {
      if (!/^view details$/i.test(textOf(a)) || a.getAttribute('aria-label')) { return; }
      var art = a.closest('.article, article, .blog-post, .blog_post, [class*="article"]');
      var t = art ? textOf(art.querySelector('h1, h2, h3, h4, .title')) : '';
      if (t) { setAttr(a, 'aria-label', 'View details: ' + t); }
    });
  }

  /* =========================================================================
     10. Reviews app (Judge.me) - WCAG 2.1.1 / 2.4.3 / 2.4.7 / 4.1.2
     ====================================================================== */
  function jdgmScoreLabel(el) {
    var score = el.getAttribute('data-score') || '';
    var m = String(score).match(/[\d.]+/);
    if (!m || parseFloat(m[0]) === 0) { return 'No rating yet'; }
    return 'Rated ' + m[0] + ' out of 5 stars';
  }
  function fixReviewsWidgets() {
    /* star badge inside product card links: it was a focusable "button"
       nested in a link. It becomes an image that states the rating. */
    each('.jdgm-prev-badge__stars', function (s) {
      setAttr(s, 'role', 'img');
      setAttr(s, 'aria-label', jdgmScoreLabel(s));
      if (s.getAttribute('tabindex') !== '-1') { s.setAttribute('tabindex', '-1'); }
    });
    /* review carousel: the star rows are images, not tab stops */
    each('.jdgm-carousel-item__review-rating[tabindex]', function (r) { r.removeAttribute('tabindex'); });
    each('a.jdgm-carousel-item__product', function (a) {
      if (a.getAttribute('aria-label')) { return; }
      var card = a.closest('.jdgm-carousel-item') || a.parentElement;
      var title = card && card.querySelector('.jdgm-carousel-item__product-title, .jdgm-carousel-item__product-url, img[alt]');
      var name = title ? (title.getAttribute('alt') || textOf(title)) : '';
      if (!name) {
        var href = a.getAttribute('href') || '';
        name = decodeURIComponent(href.split('#')[0].split('/').pop() || '').replace(/-/g, ' ');
      }
      if (name) { setAttr(a, 'aria-label', 'Read reviews for ' + name); }
    });
    /* The store hides the product name in the review carousel, so the
       product link there has nothing to show and no height. As a keyboard
       stop it showed only a thin line, so while it has no size it is left
       out of the tab order and hidden from screen readers, just as it is
       hidden from sight. If the name is shown again, the link comes back. */
    each('a.jdgm-carousel-item__product', function (a) {
      var empty = a.getBoundingClientRect().height < 2;
      if (empty && a.getAttribute('data-ada-empty') !== '1') {
        a.setAttribute('data-ada-empty', '1');
        a.setAttribute('tabindex', '-1');
        a.setAttribute('aria-hidden', 'true');
      } else if (!empty && a.getAttribute('data-ada-empty') === '1') {
        a.removeAttribute('data-ada-empty');
        a.removeAttribute('tabindex');
        a.removeAttribute('aria-hidden');
      }
    });
    /* carousel arrows are rotated squares; a ring on them is a diamond. A
       transparent, unrotated button sits over each arrow and carries the ring. */
    each('.jdgm-carousel__left-arrow, .jdgm-carousel__right-arrow', function (arrow) {
      var wrap = arrow.parentElement;
      if (!wrap) { return; }
      var btn = arrow.__adaBtn;
      if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ada-jdgm-arrow';
        btn.setAttribute('data-ada-generated', '1');
        btn.setAttribute('aria-label', /left/.test(arrow.className) ? 'Previous review' : 'Next review');
        btn.addEventListener('click', function (ev) { ev.preventDefault(); arrow.click(); });
        wrap.insertBefore(btn, arrow.nextSibling);
        arrow.__adaBtn = btn;
        arrow.setAttribute('tabindex', '-1');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.removeAttribute('role');
        if (window.getComputedStyle(wrap).position === 'static') { wrap.style.position = 'relative'; }
      }
      var wr = wrap.getBoundingClientRect(), ar = arrow.getBoundingClientRect();
      var size = Math.max(44, Math.round(Math.max(ar.width, ar.height) * 1.5));
      btn.style.width = size + 'px';
      btn.style.height = size + 'px';
      btn.style.left = Math.round(ar.left - wr.left + ar.width / 2 - size / 2) + 'px';
      btn.style.top = Math.round(ar.top - wr.top + ar.height / 2 - size / 2) + 'px';
      btn.style.display = isShown(arrow) ? '' : 'none';
    });
    syncReviewCarousels();
    wireReviewCarouselPause();
  }
  /* WCAG 2.2.2 Pause, Stop, Hide - the review carousel rotates on its own
     every few seconds. The app pauses it while the pointer is over it; the
     same pause now happens while keyboard focus is inside it, a pause button
     (visible when it receives focus) lets anyone stop the rotation, and the
     rotation is stopped from the start for anyone who has asked their
     device for reduced motion. */
  function wireReviewCarouselPause() {
    each('.jdgm-carousel-wrapper', function (wrap) {
      if (wrap.__adaPauseWired) { return; }
      var car = wrap.querySelector('.jdgm-carousel');
      if (!car) { return; }
      wrap.__adaPauseWired = true;
      var paused = false;
      var targets = [wrap, car];
      var send = function (types) {
        for (var i = 0; i < targets.length; i++) { for (var k = 0; k < types.length; k++) { fireMouse(targets[i], types[k]); } }
      };
      var hold = function () { send(['pointerenter', 'mouseenter', 'mouseover']); };
      var release = function () { if (!paused) { send(['pointerleave', 'mouseleave', 'mouseout']); } };
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ada-sr-only ada-carousel-pause';
      btn.setAttribute('data-ada-generated', '1');
      var setLabel = function () {
        btn.textContent = paused ? 'Resume automatic rotation of reviews' : 'Pause automatic rotation of reviews';
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      };
      btn.addEventListener('click', function () {
        paused = !paused;
        setLabel();
        if (paused) { hold(); } else { send(['pointerleave', 'mouseleave', 'mouseout']); }
      });
      setLabel();
      wrap.insertBefore(btn, wrap.firstChild);
      wrap.addEventListener('focusin', hold);
      wrap.addEventListener('focusout', function (e) {
        if (e.relatedTarget && wrap.contains(e.relatedTarget)) { return; }
        release();
      });
      /* the app resumes when the pointer leaves; keep it stopped if the
         shopper asked for that */
      wrap.addEventListener('mouseleave', function () { if (paused) { window.setTimeout(hold, 30); } });
      try {
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          paused = true;
          setLabel();
          hold();
          window.setTimeout(hold, 1500);
        }
      } catch (e) { /* ignore */ }
    });
  }
  /* cards that are scrolled out of the carousel viewport are hidden from the
     keyboard and from screen readers */
  function syncReviewCarousels() {
    each('.jdgm-carousel-wrapper', function (car) {
      var vpEl = car.querySelector('.jdgm-carousel__viewport, .jdgm-carousel') || car;
      var vp = vpEl.getBoundingClientRect();
      each('.jdgm-carousel-item', function (item) {
        var r = item.getBoundingClientRect();
        var visible = r.width > 0 && r.right > vp.left + 4 && r.left < vp.right - 4;
        setAttr(item, 'aria-hidden', visible ? 'false' : 'true');
        each('a, button, [tabindex]', function (f) {
          if (f.classList.contains('ada-jdgm-arrow') || f.getAttribute('data-ada-empty') === '1') { return; }
          if (visible) {
            if (f.getAttribute('data-ada-cti') !== null) {
              var v = f.getAttribute('data-ada-cti');
              if (v === '') { f.removeAttribute('tabindex'); } else { f.setAttribute('tabindex', v); }
              f.removeAttribute('data-ada-cti');
            }
          } else if (f.getAttribute('data-ada-cti') === null) {
            f.setAttribute('data-ada-cti', f.getAttribute('tabindex') === null ? '' : f.getAttribute('tabindex'));
            f.setAttribute('tabindex', '-1');
          }
        }, item);
      }, car);
      if (car.getAttribute('data-ada-car') !== '1') {
        car.setAttribute('data-ada-car', '1');
        car.addEventListener('focusin', function () {
          /* undo the browser's scroll-into-view, which breaks the slider's transform */
          each('.jdgm-carousel__viewport, .jdgm-carousel, .jdgm-carousel-wrapper', function (v) { if (v.scrollLeft) { v.scrollLeft = 0; } }, car);
          if (car.scrollLeft) { car.scrollLeft = 0; }
        });
        try {
          new MutationObserver(debounce(syncReviewCarousels, 80)).observe(car, { attributes: true, subtree: true, attributeFilter: ['style', 'class'] });
        } catch (e) { /* ignore */ }
      }
    });
  }

  /* =========================================================================
     11. Page builder product boxes (home page) - WCAG 2.1.1 / 4.1.2
         The product boxes are built from divs with a data-href attribute and
         a click handler, so they were not links. The product title now wraps
         a real link to the product.
     ====================================================================== */
  function fixPageBuilderProductBoxes() {
    each('[data-pf-type="ProductBox"]', function (box) {
      if (box.querySelector('a.ada-pf-product-link')) { return; }
      var title = box.querySelector('[data-pf-type="ProductTitle"]');
      if (!title || title.querySelector('a')) { return; }
      var href = title.getAttribute('data-href') || '';
      if (!href) {
        var img = box.querySelector('[data-href]');
        href = img ? img.getAttribute('data-href') : '';
      }
      if (!href) { return; }
      var a = document.createElement('a');
      a.className = 'ada-pf-product-link';
      a.setAttribute('data-ada-generated', '1');
      a.href = href;
      while (title.firstChild) { a.appendChild(title.firstChild); }
      title.appendChild(a);
    });
  }

  /* =========================================================================
     12. Product cards (collection, search, cart, related products)
         WCAG 2.4.3 / 2.4.4
         Each card has an image link and a caption link to the same product.
         The image link leaves the tab order and the accessibility tree, so
         each product is one stop named by its caption.
     ====================================================================== */
  function fixProductCards() {
    each('.product-wrap, .product_card, .product-index, .search_result', function (card) {
      var links = card.querySelectorAll('a[href]');
      if (links.length < 2) { return; }
      var caption = null;
      for (var i = 0; i < links.length; i++) {
        if (links[i].classList.contains('product-info__caption') || (visibleText(links[i]) && !links[i].querySelector('img'))) { caption = links[i]; break; }
      }
      if (!caption) { return; }
      for (var j = 0; j < links.length; j++) {
        var l = links[j];
        if (l === caption || caption.contains(l) || l.contains(caption)) { continue; }
        if (l.getAttribute('href') === caption.getAttribute('href') || l.querySelector('img')) {
          setAttr(l, 'tabindex', '-1');
          setAttr(l, 'aria-hidden', 'true');
        }
      }
    });
  }

  /* =========================================================================
     13. Wishlist button - WCAG 2.1.1 / 4.1.2
         The wishlist app renders its button as an anchor with no href.
     ====================================================================== */
  function fixWishlist() {
    each('a.flits-wls-button:not([href])', function (b) {
      setAttr(b, 'role', 'button');
      setAttr(b, 'tabindex', '0');
      var added = /flits-wls-active|flits-wls-added/.test(b.className) && !/flits-wls-inactive/.test(b.className);
      setAttr(b, 'aria-pressed', added ? 'true' : 'false');
    });
  }

  /* =========================================================================
     14. Flickity carousels (product gallery, recently viewed, related)
         WCAG 2.4.3 / 4.1.2
         The carousel containers carried tabindex="0" and were all named
         "Product images". They are no longer tab stops (the arrow buttons
         remain) and each is named after its section. A card that receives
         keyboard focus is brought into view by the carousel, not the browser.
     ====================================================================== */
  function flickityFor(el) {
    try { if (window.Flickity && window.Flickity.data) { return window.Flickity.data(el); } } catch (e) { /* ignore */ }
    try { if (window.jQuery) { return window.jQuery(el).data('flickity'); } } catch (e) { /* ignore */ }
    return null;
  }
  function fixFlickity() {
    each('.flickity-enabled', function (car) {
      if (car.getAttribute('tabindex') === '0') { car.setAttribute('tabindex', '-1'); }
      var label = 'Product images';
      if (car.classList.contains('product_gallery_nav')) { label = 'Product image thumbnails'; }
      else if (!car.classList.contains('product_gallery')) {
        /* the nearest heading before the carousel names it */
        var h = null, n = car;
        while (n && !h && n.tagName !== 'BODY') {
          var s = n.previousElementSibling;
          while (s && !h) {
            if (/^H[1-6]$/.test(s.tagName)) { h = s; }
            else if (s.querySelector) { var inner = s.querySelectorAll('h1, h2, h3, h4'); if (inner.length) { h = inner[inner.length - 1]; } }
            s = s.previousElementSibling;
          }
          n = n.parentElement;
        }
        label = h && textOf(h) ? textOf(h) : 'Product carousel';
      }
      setAttr(car, 'role', 'group');
      setAttr(car, 'aria-roledescription', 'carousel');
      setAttr(car, 'aria-label', label);
      each('.flickity-button', function (b) {
        if (!b.getAttribute('aria-label')) { setAttr(b, 'aria-label', /next/.test(b.className) ? 'Next' : 'Previous'); }
      }, car);
      if (car.getAttribute('data-ada-flk') === '1') { return; }
      car.setAttribute('data-ada-flk', '1');
      car.addEventListener('focusin', function (e) {
        var vp = car.querySelector('.flickity-viewport');
        if (vp && vp.scrollLeft) { vp.scrollLeft = 0; }
        var flk = flickityFor(car);
        if (!flk || !flk.slider) { return; }
        var target = e.target;
        while (target && target.parentElement !== flk.slider) { target = target.parentElement; }
        if (!target) { return; }
        var cr = target.getBoundingClientRect(), vr = (vp || car).getBoundingClientRect();
        if (cr.left < vr.left - 2 || cr.right > vr.right + 2) {
          try { flk.selectCell(target, false, true); } catch (err) { /* ignore */ }
        }
      });
    });
    each('.gallery-cell[aria-selected], .flickity-slider [aria-selected]', function (c) { c.removeAttribute('aria-selected'); });
  }

  /* =========================================================================
     15. Custom slime builder (product builder app) - WCAG 2.4.3 / 4.1.2
         The preview canvases carried tabindex="1", which pulled them ahead of
         everything else in the tab order. Category headers were focusable but
         announced as nothing.
     ====================================================================== */
  function fixProductBuilder() {
    each('.cpb-render-konva[tabindex]', function (c) { if (c.getAttribute('tabindex') !== '-1') { c.setAttribute('tabindex', '-1'); } });
    each('.cpb-category-title__inner[tabindex]', function (t) {
      setAttr(t, 'role', 'button');
      var cat = t.closest('[class*="cpb-category"]');
      var collapsed = cat && /collapsed|closed/.test(cat.className);
      setAttr(t, 'aria-expanded', collapsed ? 'false' : 'true');
    });
    each('.cpb-agreement-checkbox', function (cb) {
      if (!cb.getAttribute('aria-label') && !cb.closest('label')) { setAttr(cb, 'aria-label', 'I agree to the terms of sale'); }
    });
  }

  /* =========================================================================
     16. Images - WCAG 1.1.1
         Retailer logos on the store locator carried file names as alternative
         text; app icons had no alt attribute at all.
     ====================================================================== */
  function cleanAlt(alt) {
    var a = alt.replace(/__PID:.*$/i, '').replace(/\.(png|jpe?g|gif|webp|svg)(\.png)?$/i, '').replace(/e\d{10,}/g, '').replace(/[-_]+/g, ' ').replace(/\s*\(\d+\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
    if (!a || /^(images?|img|image \d+|logo)$/i.test(a)) { return 'Retailer logo'; }
    if (!/logo/i.test(a)) { a += ' logo'; }
    return a.charAt(0).toUpperCase() + a.slice(1);
  }
  function fixImageAlts() {
    each('img[alt*="__PID:"], img[alt$=".png"], img[alt$=".jpg"], img[alt$=".jpeg"], img[alt$=".webp"], img[alt$=".gif"], img[alt$=".svg"]', function (img) {
      img.setAttribute('alt', cleanAlt(img.getAttribute('alt')));
    });
    each('img[class*="flits-icon"]', function (img) {
      if (!img.hasAttribute('alt')) { setAttr(img, 'alt', ''); }
      setAttr(img, 'aria-hidden', 'true');
    });
    each('img', function (im) {
      if (im.getAttribute('alt') !== null) { return; }
      if (im.getAttribute('aria-hidden') === 'true' || im.getAttribute('role') === 'presentation' || im.getAttribute('role') === 'none') { return; }
      var host = im.closest('a, button, [role="button"], [role="link"]');
      if (host && textOf(host)) { im.setAttribute('alt', ''); return; }
      var m = String(im.className || '').match(/(?:icon|logo)[-_]([a-z0-9]+)/i);
      if (m) { im.setAttribute('alt', m[1].charAt(0).toUpperCase() + m[1].slice(1)); return; }
      var t = (im.getAttribute('title') || '').trim();
      if (t) { im.setAttribute('alt', t); return; }
      var lbl = im.getAttribute('aria-label');
      if (lbl && lbl.trim()) { im.setAttribute('alt', lbl.trim()); return; }
      im.setAttribute('alt', '');
    });
    /* a scrolling logo strip must be reachable by the keyboard (scrollable region) */
    each('.glider', function (g) {
      if (g.querySelector(FOCUSABLE)) { return; }
      setAttr(g, 'tabindex', '0');
      setAttr(g, 'role', 'region');
      if (!g.getAttribute('aria-label')) { setAttr(g, 'aria-label', 'Retailer logos'); }
    });
  }

  /* =========================================================================
     17. Markup injected by apps and the page builder
     ====================================================================== */
  function fixAppMarkup() {
    /* PageFly list items that are not inside a list. WCAG 1.3.1. */
    each('li[data-pf-type="List.Item"]', function (li) {
      var p = li.parentElement;
      if (!p || p.tagName === 'UL' || p.tagName === 'OL') { return; }
      if (p.getAttribute('role') === 'list') { return; }
      var kids = p.children, allLi = kids.length > 0;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].tagName !== 'LI') { allLi = false; break; }
      }
      if (allLi) { setAttr(p, 'role', 'list'); } else { setAttr(li, 'role', 'none'); }
    });
    /* Footer headings generated by the page builder. WCAG 1.3.1. */
    each('footer h5[data-pf-type="Heading"], footer h4[data-pf-type="Heading"], footer h6[data-pf-type="Heading"]', function (h) {
      var link = h.querySelector('a');
      if (link && textOf(link) === textOf(h)) { setAttr(h, 'role', 'none'); } else { setAttr(h, 'aria-level', '2'); }
    });
    /* autocomplete tokens on identity fields. WCAG 1.3.5. */
    each('input:not([autocomplete]), textarea:not([autocomplete])', function (f) {
      var hint = ((f.name || '') + ' ' + (f.id || '') + ' ' + (f.placeholder || '')).toLowerCase();
      var token = '';
      if (f.type === 'email' || /e-?mail/.test(hint)) { token = 'email'; }
      else if (f.type === 'tel' || /phone|tel\b/.test(hint)) { token = 'tel'; }
      else if (/full ?name/.test(hint)) { token = 'name'; }
      else if (/first ?name|given/.test(hint)) { token = 'given-name'; }
      else if (/last ?name|surname|family/.test(hint)) { token = 'family-name'; }
      else if (/zip|postcode|postal/.test(hint)) { token = 'postal-code'; }
      if (token) { setAttr(f, 'autocomplete', token); }
    });
    /* Headings generated by the page builder skip levels. WCAG 1.3.1. */
    (function () {
      var heads = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      var prev = 0;
      for (var i = 0; i < heads.length; i++) {
        var h = heads[i];
        if (h.getAttribute('role') === 'none') { continue; }
        var lvl = parseInt(h.getAttribute('aria-level') || h.tagName.charAt(1), 10);
        if (prev && lvl > prev + 1 && h.getAttribute('data-pf-type') === 'Heading') {
          lvl = prev + 1;
          setAttr(h, 'aria-level', String(lvl));
        }
        prev = lvl;
      }
    })();
    /* Links that open a new tab should say so. WCAG 2.4.4 / 3.2.5. */
    each('a[target="_blank"]', function (a) {
      if (a.getAttribute('data-ada-newtab') === '1') { return; }
      a.setAttribute('data-ada-newtab', '1');
      var name = a.getAttribute('aria-label') || a.getAttribute('title') || textOf(a);
      if (!name) { var im = a.querySelector('img[alt]'); name = im ? im.getAttribute('alt') : ''; }
      if (name && !/opens in a new/i.test(name)) { setAttr(a, 'aria-label', name + ' (opens in a new tab)'); }
      if (!/noopener/.test(a.getAttribute('rel') || '')) {
        a.setAttribute('rel', ((a.getAttribute('rel') || '') + ' noopener').trim());
      }
    });
    /* A missing translation must never be read out as a field name. The
       only place the theme leaves one is the quantity field, so the rule is
       held to quantity inputs rather than every labelled element. */
    each('input[aria-label^="Translation missing"], select[aria-label^="Translation missing"]', function (el) {
      var raw = el.getAttribute('aria-label') || '';
      if (!/quantity/i.test(raw) && !/quantity/i.test(el.name || '') && !/quantity|qty/i.test(el.className || '')) { return; }
      var l = raw.replace(/^Translation missing:[^,]*,?\s*/, '');
      setAttr(el, 'aria-label', ('Quantity' + (l ? ', ' + l : '')).trim());
    });
    /* Slide cart fields the app leaves unnamed. WCAG 1.3.1 / 4.1.2. */
    each('#slidecarthq select, .slidecarthq select', function (s) {
      if (s.getAttribute('aria-label') || (s.id && document.querySelector('label[for="' + s.id + '"]')) || s.closest('label')) { return; }
      var item = s.closest('.upsell, .upsell-item, [class*="upsell"]');
      var t = item ? textOf(item.querySelector('a, .title, [class*="title"]')) : '';
      setAttr(s, 'aria-label', t ? 'Choose options for ' + t : 'Choose an option');
    });
    /* The name of the line a control belongs to inside the slide cart. */
    function slideCartItemName(el) {
      var row = el.closest('.item, .item-container, .upsell, .upsell-item, [class*="upsell"]');
      if (!row) { return ''; }
      var link = row.querySelector('a[href*="/products/"]');
      var t = link ? textOf(link) : '';
      if (!t) {
        var title = row.querySelector('.title, [class*="title"], .item-title');
        t = title ? textOf(title) : '';
      }
      return t;
    }
    /* The quantity box on each slide cart line carries no label at all. */
    each('#slidecarthq input[type="text"], .slidecarthq input[type="text"], #slidecarthq input[type="number"], .slidecarthq input[type="number"]', function (q) {
      if (!q.closest('.quantity-selector, .quantity, [class*="quantity"]')) { return; }
      if (q.getAttribute('aria-label') || (q.id && document.querySelector('label[for="' + q.id + '"]')) || q.closest('label')) { return; }
      var t = slideCartItemName(q);
      setAttr(q, 'aria-label', t ? 'Quantity, ' + t : 'Quantity');
    });
    /* Each line shows the picture and the name as two links to the same
       product. The picture link has no text, so it is taken out of the tab
       order and out of the reading order and the named link is left to do
       the job. WCAG 2.4.4 / 4.1.2. */
    each('#slidecarthq a[href*="/products/"], .slidecarthq a[href*="/products/"]', function (a) {
      if (visibleText(a)) { return; }
      if (a.getAttribute('aria-label') || a.getAttribute('title')) { return; }
      var row = a.closest('.item, .item-container, .upsell, .upsell-item, [class*="upsell"]');
      var twin = row ? [].slice.call(row.querySelectorAll('a[href*="/products/"]')).filter(function (o) {
        return o !== a && visibleText(o);
      })[0] : null;
      if (twin) {
        setAttr(a, 'aria-hidden', 'true');
        setAttr(a, 'tabindex', '-1');
      } else {
        var t = slideCartItemName(a);
        if (t) { setAttr(a, 'aria-label', t); }
      }
    });
    /* The add buttons on the suggested products all read as "Add". */
    each('#slidecarthq button, .slidecarthq button', function (b) {
      if (b.getAttribute('aria-label')) { return; }
      var t = textOf(b);
      if (!/^add$/i.test(t)) { return; }
      var name = slideCartItemName(b);
      if (name) { setAttr(b, 'aria-label', 'Add ' + name + ' to cart'); }
    });
    /* The shipping protection switch is a bare checkbox inside a label that
       holds no words, so it reaches a screen reader with no name. */
    each('#shipping-protection, #slidecarthq input[type="checkbox"], .slidecarthq input[type="checkbox"]', function (c) {
      if (c.getAttribute('aria-label') || c.getAttribute('aria-labelledby')) { return; }
      var lab = c.id ? document.querySelector('label[for="' + c.id + '"]') : null;
      if (lab && textOf(lab)) { return; }
      var wrap = c.closest('label');
      if (wrap && textOf(wrap)) { return; }
      var block = c.closest('.shipping-protection, [class*="shipping-protect"]');
      var words = block ? textOf(block) : '';
      setAttr(c, 'aria-label', words ? words.slice(0, 90) : 'Add shipping protection to this order');
    });
  }

  /* =========================================================================
     18. Page builder form fields - WCAG 1.3.1 / 3.3.2
         The page builder gives its fields a placeholder and nothing else. The
         field's own name attribute carries the wording the merchant typed,
         for example contact[Full Name], so that is what is used.
     ====================================================================== */
  function labelFromName(raw) {
    var s = String(raw || '');
    var m = s.match(/\[([^\]]+)\]/);
    if (m) { s = m[1]; }
    s = s.replace(/[_-]+/g, ' ').trim();
    if (!s) { return ''; }
    if (s.toLowerCase() === 'email') { return 'Email'; }
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  function labelPageBuilderFields() {
    each('.__pf input, .__pf textarea, .__pf select', function (el) {
      var type = (el.getAttribute('type') || '').toLowerCase();
      if (type === 'hidden' || type === 'submit' || type === 'button') { return; }
      if (type === 'radio' || type === 'checkbox') { return; }
      if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) { return; }
      if (el.id && document.querySelector('label[for="' + el.id + '"]')) { return; }
      if (el.closest('label')) { return; }
      var name = labelFromName(el.getAttribute('name'));
      if (!name) { name = (el.getAttribute('placeholder') || '').trim(); }
      if (!name) { return; }
      if (el.hasAttribute('required') && name.indexOf('required') < 0) { name = name + ', required'; }
      el.setAttribute('aria-label', name);
    });
    var groups = {};
    each('.__pf input[type="radio"]', function (r) {
      var n = r.getAttribute('name');
      if (!n) { return; }
      if (!groups[n]) { groups[n] = []; }
      groups[n].push(r);
    });
    Object.keys(groups).forEach(function (n) {
      var set = groups[n];
      if (set.length < 2) { return; }
      var label = labelFromName(n);
      if (!label) { return; }
      var box = set[0].parentElement;
      while (box && !set.every(function (r) { return box.contains(r); })) { box = box.parentElement; }
      if (!box) { return; }
      if (box.getAttribute('role') === 'radiogroup') { return; }
      box.setAttribute('role', 'radiogroup');
      box.setAttribute('aria-label', label);
      if (set[0].hasAttribute('required')) { box.setAttribute('aria-required', 'true'); }
    });
  }

  /* =========================================================================
     19. Ids, frames, headings, names - WCAG 4.1.1 / 4.1.2 / 1.3.1
     ====================================================================== */
  function dedupeGeneratedIds() {
    var groups = {};
    each('[id^="ada-"]', function (el) {
      if (!groups[el.id]) { groups[el.id] = []; }
      groups[el.id].push(el);
    });
    Object.keys(groups).forEach(function (id) {
      var set = groups[id];
      if (set.length < 2) { return; }
      var keep = null;
      var pointer = document.querySelector('[aria-controls="' + id + '"]');
      for (var i = 0; i < set.length; i++) {
        if (pointer && pointer.compareDocumentPosition && (pointer.compareDocumentPosition(set[i]) & 4)) { keep = set[i]; break; }
      }
      if (!keep) { keep = set[0]; }
      for (var k = 0; k < set.length; k++) {
        if (set[k] !== keep) { set[k].removeAttribute('id'); }
      }
    });
  }
  function unheadLinkOnlyHeadings() {
    each('footer h1, footer h2, footer h3, footer h4, footer h5, footer h6', function (h) {
      if (h.getAttribute('aria-level') || h.getAttribute('role')) { return; }
      if (h.children.length !== 1) { return; }
      if (h.children[0].tagName !== 'A') { return; }
      if (textOf(h) !== textOf(h.children[0])) { return; }
      if (!textOf(h)) { return; }
      h.setAttribute('role', 'presentation');
    });
  }
  function dedupeSvgIds() {
    var map = {};
    each('svg [id], svg[id]', function (e) {
      if (!e.id) { return; }
      if (!map[e.id]) { map[e.id] = []; }
      map[e.id].push(e);
    });
    var html = null;
    Object.keys(map).forEach(function (id) {
      var list = map[id];
      if (list.length < 2) { return; }
      if (document.querySelectorAll('#' + CSS.escape(id)).length !== list.length) { return; }
      if (html === null) { html = document.documentElement.innerHTML; }
      var esc = id.replace(/[.*+?^$\{}()|[\]\\]/g, '\\$&');
      var re = new RegExp('(href|xlink:href)\\s*=\\s*["\']#' + esc + '["\']|url\\(["\']?#' + esc + '["\']?\\)|aria-[a-z-]+\\s*=\\s*["\'][^"\']*' + esc + '|for\\s*=\\s*["\']' + esc + '["\']');
      if (re.test(html)) { return; }
      for (var i = 1; i < list.length; i++) { list[i].removeAttribute('id'); }
    });
  }
  function dedupeDocumentIds() {
    var map = {};
    each('[id]', function (e) {
      if (!e.id) { return; }
      if (!map[e.id]) { map[e.id] = []; }
      map[e.id].push(e);
    });
    var html = null;
    /* ids the apps' own stylesheets and scripts key on are never renamed */
    var skip = /^(shopify-|pf-|google|fb_|tidio|gorgias|slidecarthq|shipping-protection|product-builder|jdgm|snize)/;
    Object.keys(map).forEach(function (id) {
      var list = map[id];
      if (list.length < 2) { return; }
      if (skip.test(id)) { return; }
      if (html === null) { html = document.documentElement.innerHTML; }
      var esc = id.replace(/[.*+?^$\{}()|[\]\\]/g, '\\$&');
      var re = new RegExp('(href|xlink:href)\\s*=\\s*["\']#' + esc + '["\']|url\\(["\']?#' + esc + '["\']?\\)|(aria-[a-z-]+|for|form|list|headers)\\s*=\\s*["\'][^"\']*' + esc);
      if (re.test(html)) { return; }
      for (var i = 1; i < list.length; i++) {
        var n = id + '-ada-dup' + i;
        while (document.getElementById(n)) { n = n + 'x'; }
        list[i].id = n;
      }
    });
  }
  function nameEmbeddedFrames() {
    each('iframe', function (f) {
      if (f.getAttribute('aria-hidden') === 'true') { return; }
      var r = f.getBoundingClientRect();
      if (r.width <= 8 && r.height <= 8) { return; }
      if (!f.offsetParent && f.offsetWidth === 0 && f.offsetHeight === 0) { return; }
      var t = (f.getAttribute('title') || '').trim();
      if (t && !/^[a-z0-9_\-]+$/.test(t)) { return; }
      var src = f.getAttribute('src') || f.getAttribute('data-src') || '';
      var kind = 'Embedded content';
      if (/youtu|vimeo|wistia|player|video/i.test(src)) { kind = 'Video'; }
      else if (/instagram/i.test(src)) { kind = 'Instagram post'; }
      else if (/facebook\.com\/plugins\/share/i.test(src)) { kind = 'Share on Facebook'; }
      else if (/facebook/i.test(src)) { kind = 'Facebook content'; }
      else if (/twitter|x\.com/i.test(src)) { kind = 'Post'; }
      else if (/tiktok/i.test(src)) { kind = 'TikTok video'; }
      else if (/(maps\.google|google\.com\/maps)/i.test(src)) { kind = 'Map'; }
      var head = '';
      var p = f.parentElement, hops = 0;
      while (p && hops < 3 && !head) {
        var h = p.querySelector('h1, h2, h3, h4, h5, h6');
        if (h) { head = textOf(h); }
        p = p.parentElement; hops++;
      }
      f.setAttribute('title', head ? (kind + ': ' + head.slice(0, 70)) : kind);
      if (f.getAttribute('data-ada-fr') !== '1') {
        f.setAttribute('data-ada-fr', '1');
        f.addEventListener('focus', function () {
          if (Date.now() - lastTabAt > 900 || Date.now() - lastPointerAt < 500) { return; }
          if (f.className.indexOf('ada-frame-focus') < 0) { f.className = f.className + ' ada-frame-focus'; }
        });
        f.addEventListener('blur', function () {
          f.className = f.className.replace(/\s*ada-frame-focus\b/g, '');
        });
      }
    });
  }
  function normaliseHeadingLevels() {
    var prev = 0;
    each('h1, h2, h3, h4, h5, h6', function (h) {
      var role = h.getAttribute('role');
      if (role === 'presentation' || role === 'none') { return; }
      var r = h.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) { return; }
      var lvl = parseInt(h.getAttribute('aria-level'), 10) || parseInt(h.tagName.slice(1), 10);
      if (prev && lvl > prev + 1) {
        lvl = prev + 1;
        h.setAttribute('aria-level', String(lvl));
      }
      prev = lvl;
    });
  }
  function labelPlaceholderOnlyFields() {
    each('input, select, textarea', function (f) {
      if (f.type === 'hidden' || f.disabled) { return; }
      if (f.getAttribute('aria-label') || f.getAttribute('aria-labelledby')) { return; }
      if (f.id) {
        var esc = (window.CSS && CSS.escape) ? CSS.escape(f.id) : f.id;
        var lab = document.querySelector('label[for="' + esc + '"]');
        if (lab && textOf(lab)) { return; }
      }
      var wrap = f.closest('label');
      if (wrap && textOf(wrap)) { return; }
      var ph = (f.getAttribute('placeholder') || f.getAttribute('title') || '').trim();
      if (!ph) { return; }
      f.setAttribute('aria-label', ph.replace(/\.{2,}\s*$/, '').trim());
    });
  }
  function nameEmptyControls() {
    each('a[href], button, [role="button"], [role="link"]', function (el) {
      if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')) { return; }
      if (textOf(el).replace(/[\u200b\u200c\u200d\u200e\u200f\u00a0\ufeff\u2060]+/g, '').trim()) { return; }
      var sv = el.querySelector('svg title');
      if (sv && textOf(sv)) { return; }
      var pic = el.querySelector('img');
      if (pic && (pic.getAttribute('alt') || '').trim()) {
        var pr = pic.getBoundingClientRect();
        var pcs = window.getComputedStyle(pic);
        if (pr.width > 0 && pr.height > 0 && pcs.display !== 'none' && pcs.visibility !== 'hidden') { return; }
      }
      var t = (el.getAttribute('title') || '').trim();
      if (t) { return; }
      var cls = String(el.className || '');
      var label = '';
      if (/jdgm-rev__pic-link|jdgm-rev__pic/.test(cls)) { label = 'Review photo'; }
      else if (/jdgm-rev__prod-link/.test(cls)) { label = 'Product reviewed'; }
      else if (/prev|left-arrow|previous/i.test(cls)) { label = 'Previous'; }
      else if (/next|right-arrow/i.test(cls)) { label = 'Next'; }
      else if (/close|dismiss/i.test(cls)) { label = 'Close'; }
      else if (/cart/i.test(cls)) { label = 'Cart'; }
      else if (/search/i.test(cls)) { label = 'Search'; }
      else if (/menu/i.test(cls)) { label = 'Menu'; }
      if (!label) {
        var im = el.querySelector('img');
        if (im) {
          var src = String(im.getAttribute('src') || im.getAttribute('data-src') || '');
          var base = src.split('?')[0].split('/').pop().replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim();
          if (base && base.length < 60 && !/^data:/.test(src)) { label = base.charAt(0).toUpperCase() + base.slice(1); }
        }
      }
      if (!label) {
        var href = String(el.getAttribute('href') || '');
        if (href && href.charAt(0) !== '#' && href.indexOf('javascript:') !== 0) {
          var seg = href.split('?')[0].replace(/\/$/, '').split('/').pop().replace(/[-_]+/g, ' ').trim();
          if (seg && seg.length < 60) { label = seg.charAt(0).toUpperCase() + seg.slice(1); }
        }
      }
      if (label) { el.setAttribute('aria-label', label); }
    });
  }
  function hideEmptyDecorativeLinks() {
    each('a[href]', function (a) {
      var lbl = ((a.getAttribute('aria-label') || '') + (a.getAttribute('title') || '')).replace(/[\s\u200b\u200c\u200d\u200e\u200f\u00a0\ufeff\u2060]+/g, '');
      if (lbl || a.getAttribute('aria-labelledby')) { return; }
      var t = (a.textContent || '').replace(/[\s\u200b\u200c\u200d\u200e\u200f\u00a0\ufeff\u2060]+/g, '');
      if (t) { return; }
      if (a.querySelector('img, svg, picture, video, canvas')) { return; }
      var href = String(a.getAttribute('href') || '');
      if (!(href === '' || href === '#' || href.indexOf('javascript:') === 0)) { return; }
      if (a === document.activeElement || a.contains(document.activeElement)) { return; }
      a.setAttribute('tabindex', '-1');
      a.setAttribute('aria-hidden', 'true');
    });
  }

  /* =========================================================================
     20. Header cart count kept in step with the cart - WCAG 4.1.2
         The slide cart app adds to the cart with its own AJAX call and
         repaints only its own drawer. Every cart write goes through
         /cart/add, /cart/change, /cart/update or /cart/clear, so those calls
         are watched and the authoritative count is read back from /cart.js.
     ====================================================================== */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var cartTimer = null;
  var lastCount = null;
  function cartLinks() {
    var out = [];
    var all = document.querySelectorAll('a[href]');
    for (var i = 0; i < all.length; i++) {
      var a = all[i];
      var p;
      try { p = new URL(a.href, location.origin).pathname; } catch (e) { continue; }
      if (/(^|\/)cart\/?$/.test(p)) { out.push(a); }
    }
    var counts = document.querySelectorAll('.cart_count');
    for (var j = 0; j < counts.length; j++) {
      var up = counts[j].closest ? counts[j].closest('a') : null;
      if (up && out.indexOf(up) < 0) { out.push(up); }
    }
    return out;
  }
  function applyCount(n) {
    var label = cartName(n);
    var counts = document.querySelectorAll('.cart_count');
    for (var i = 0; i < counts.length; i++) {
      if (counts[i].textContent !== String(n)) { counts[i].textContent = String(n); }
      if (counts[i].getAttribute('aria-hidden') !== 'true') { counts[i].setAttribute('aria-hidden', 'true'); }
    }
    var links = cartLinks();
    for (var k = 0; k < links.length; k++) {
      if (links[k].getAttribute('aria-label') !== label) { links[k].setAttribute('aria-label', label); }
    }
  }
  function refreshCount() {
    if (!window.fetch) { return; }
    window.fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || typeof c.item_count !== 'number') { return; }
        if (c.item_count === lastCount) { return; }
        lastCount = c.item_count;
        applyCount(c.item_count);
        announceCart();
      })
      .catch(function () { /* ignore */ });
  }
  function scheduleCount() {
    if (cartTimer) { clearTimeout(cartTimer); }
    cartTimer = setTimeout(refreshCount, 120);
  }
  function watchCartWrites() {
    if (window.fetch && !window.fetch.__ada) {
      var nativeFetch = window.fetch;
      var wrapped = function (input, init) {
        var url = '';
        try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { /* ignore */ }
        var p = nativeFetch.apply(this, arguments);
        if (url && CART_WRITE.test(url)) {
          try { p.then(function () { scheduleCount(); }, function () { /* ignore */ }); } catch (e) { /* ignore */ }
        }
        return p;
      };
      wrapped.__ada = true;
      window.fetch = wrapped;
    }
    if (window.XMLHttpRequest && XMLHttpRequest.prototype && !XMLHttpRequest.prototype.__adaOpen) {
      var nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.__adaOpen = true;
      XMLHttpRequest.prototype.open = function (method, url) {
        try { this.__adaUrl = url; } catch (e) { /* ignore */ }
        return nativeOpen.apply(this, arguments);
      };
      var nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var self = this;
        try {
          if (self.__adaUrl && CART_WRITE.test(String(self.__adaUrl))) {
            self.addEventListener('loadend', function () { scheduleCount(); });
          }
        } catch (e) { /* ignore */ }
        return nativeSend.apply(this, arguments);
      };
    }
    var EVENTS = ['cart:updated', 'cart:refresh', 'cart:build', 'ajaxCart.afterCartLoad', 'slidecart:open', 'slidecart:updated', 'shopify:cart:updated'];
    for (var e = 0; e < EVENTS.length; e++) {
      document.addEventListener(EVENTS[e], scheduleCount, true);
      window.addEventListener(EVENTS[e], scheduleCount, true);
    }
    var first = document.querySelector('.cart_count');
    var n = first ? parseInt((first.textContent || '').replace(/[^0-9]/g, ''), 10) : NaN;
    lastCount = isNaN(n) ? null : n;
    window.addEventListener('pageshow', scheduleCount);
  }
  /* The slide cart app is loaded twice on some templates, which leaves a
     second copy of the drawer in the page. Only the first copy is driven by
     the app; the second is hidden so it can never be read, focused or seen. */
  function hideDuplicateDrawers() {
    var wraps = document.querySelectorAll('#slidecarthq, [id^="slidecarthq-ada-dup"]');
    for (var i = 1; i < wraps.length; i++) {
      var w = wraps[i];
      if (w.getAttribute('data-ada-dup-hidden') === '1') { continue; }
      w.setAttribute('data-ada-dup-hidden', '1');
      w.style.setProperty('display', 'none', 'important');
      w.setAttribute('aria-hidden', 'true');
      if ('inert' in w) { w.inert = true; }
    }
  }
  function watchDrawer() {
    var d = document.querySelector('.slidecarthq, slidecart-hq, #slidecarthq');
    if (!d || d.__adaWatched) { return; }
    d.__adaWatched = true;
    try { new MutationObserver(scheduleCount).observe(d, { childList: true, subtree: true, characterData: true }); } catch (err) { /* ignore */ }
  }

  /* =========================================================================
     Run everything
     ====================================================================== */
  /* =========================================================================
     7b. One corrected blue for every blue band and drip - WCAG 1.4.3
         The page builder paints its blue bands with the brand blue and draws
         the drips above and below them as SVG pictures with the same blue
         built in. Where a band carries white text it has to be deepened for
         contrast, and a deepened band next to a bright drip looks broken. So
         every brand blue surface - solid bands and the blue inside the drip
         pictures - moves to the same corrected blue #4f6dba already used on
         the top bar and the buttons. White text on it measures 4.97:1. The
         white in the drips and every photograph are left exactly as they are.
         Where a band's picture has the old blue printed around it, that
         printed blue is changed to the corrected blue as well (see
         recolourPhoto), so the band, its drips and the picture stay one
         surface. The review stars, and the thin borders and icons drawn in
         the old blue, use the corrected blue too, so every page shows one
         blue throughout.
     ====================================================================== */
  var BRAND_TARGET = '#4f6dba';
  var BRAND_HEX = /#(6c95f9|6b95f9|6a95f9|6c95ff)(?![0-9a-f])/gi;
  var BRAND_PCT = /rgb\(\s*41\.9\d*%\s*,\s*58\.4\d*%\s*,\s*97\.6\d*%\s*\)/gi;
  var brandSvg = {};
  function brandRgb(str) {
    var m = String(str).match(/-?[\d.]+/g);
    if (!m || m.length < 3) { return false; }
    var a = m.length > 3 ? +m[3] : 1;
    return a >= 1 && +m[0] >= 104 && +m[0] <= 110 && +m[1] >= 147 && +m[1] <= 151 && +m[2] >= 247;
  }
  function hasBrand(text) {
    return /#(6c95f9|6b95f9|6a95f9|6c95ff)(?![0-9a-f])/i.test(text) ||
      /rgb\(\s*41\.9\d*%\s*,\s*58\.4\d*%\s*,\s*97\.6\d*%\s*\)/i.test(text);
  }
  function brandSvgFor(url, done) {
    var entry = brandSvg[url];
    if (entry && entry.ready) { done(entry.data); return; }
    if (entry) { entry.waiting.push(done); return; }
    var key = 'ada-svg:' + url;
    try {
      var hit = window.sessionStorage.getItem(key);
      if (hit !== null) { brandSvg[url] = { ready: true, data: hit }; done(hit); return; }
    } catch (e) { /* storage unavailable */ }
    entry = brandSvg[url] = { ready: false, data: '', waiting: [done] };
    window.fetch(url, { credentials: 'omit' }).then(function (r) {
      return r.ok ? r.text() : '';
    }).then(function (t) {
      var data = '';
      if (t && hasBrand(t)) {
        data = 'data:image/svg+xml;charset=utf-8,' +
          encodeURIComponent(t.replace(BRAND_HEX, BRAND_TARGET).replace(BRAND_PCT, BRAND_TARGET));
      }
      try { window.sessionStorage.setItem(key, data); } catch (e) { /* storage full */ }
      entry.ready = true; entry.data = data;
      entry.waiting.forEach(function (f) { f(data); });
      entry.waiting = [];
    }).catch(function () {
      entry.ready = true;
      entry.waiting.forEach(function (f) { f(''); });
      entry.waiting = [];
    });
  }
  /* Some drips are drawn by the ::before and ::after of the band itself.
     Those cannot take an inline style, so the corrected picture is given
     to them through one small style sheet, keyed on a marker attribute. */
  var pseudoSheet = null, pseudoRules = {}, pseudoSeq = 0;
  /* The page builder writes its own picture for these edges with important on
     it, from a selector carrying three classes. The correction has to outweigh
     that, so its own attribute is repeated to carry more weight. */
  var PSEUDO_WEIGHT = '[data-ada-pb][data-ada-pb][data-ada-pb][data-ada-pb]';
  function writePseudoRules() {
    if (!pseudoSheet) {
      pseudoSheet = document.createElement('style');
      pseudoSheet.id = 'ada-brand-pseudo';
      document.head.appendChild(pseudoSheet);
    }
    pseudoSheet.textContent = BRAND_STATIC + '\n' +
      Object.keys(pseudoRules).map(function (k) { return pseudoRules[k]; }).join('\n');
  }
  /* The review app paints its stars in the old blue through its settings. */
  var BRAND_STATIC = '.jdgm-star.jdgm-star.jdgm-star.jdgm-star{color:' + BRAND_TARGET + ' !important}' +
    /* the filled part of each score bar in the review summary, painted by the
       app on a piece attached to the bar */
    '.jdgm-histogram__bar.jdgm-histogram__bar.jdgm-histogram__bar::after{background-color:' + BRAND_TARGET + ' !important}';
  function brandPseudo(el) {
    /* An app can paint one of these attached pieces after the page has
       settled, as the review widget does with its score bars, so each element
       is looked at on a few passes rather than once and for all. */
    var seen = el.__adaPseudoSeen || 0;
    if (seen >= 4) { el.__adaPseudoDone = true; return; }
    el.__adaPseudoSeen = seen + 1;
    if (el.__adaPseudoSeen >= 4) { el.__adaPseudoDone = true; }
    ['::before', '::after'].forEach(function (which) {
      var cs = window.getComputedStyle(el, which);
      if (!cs || cs.content === 'none' || cs.content === 'normal') { return; }
      /* A bar or a shape drawn as a piece attached to an element, such as the
         filled part of a review score bar, carries its colour on that piece,
         where a correction made on the element itself cannot reach it. */
      if (brandRgb(cs.backgroundColor) && !el.hasAttribute('data-ada-brand-keep')) {
        var cid = el.getAttribute('data-ada-pb');
        if (!cid) { cid = String(++pseudoSeq); el.setAttribute('data-ada-pb', cid); }
        pseudoRules[cid + which + 'bg'] = '[data-ada-pb="' + cid + '"]' + PSEUDO_WEIGHT + which +
          '{background-color:' + BRAND_TARGET + ' !important}';
        writePseudoRules();
      }
      var bi = cs.backgroundImage || 'none';
      /* A zigzag or wave edge is drawn as a gradient, which carries the brand
         blue inside the picture itself where a background colour correction
         cannot reach it. That leaves the edge in the old blue while the band
         it belongs to carries the corrected one, so the two do not match.
         Every stop painted in the brand blue takes the corrected blue. */
      if (bi !== 'none' && bi.indexOf('gradient') >= 0) {
        var swapped = bi.replace(/rgba?\([^)]*\)/g, function (colour) {
          return brandRgb(colour) ? BRAND_TARGET : colour;
        });
        if (swapped !== bi && !el.hasAttribute('data-ada-brand-keep')) {
          var gid = el.getAttribute('data-ada-pb');
          if (!gid) { gid = String(++pseudoSeq); el.setAttribute('data-ada-pb', gid); }
          pseudoRules[gid + which] = '[data-ada-pb="' + gid + '"]' + PSEUDO_WEIGHT + which +
            '{background-image:' + swapped + ' !important}';
          writePseudoRules();
        }
      }
      var m = bi.match(/url\(\s*["']?([^"')]+\.svg[^"')]*)["']?\s*\)/i);
      if (!m) { return; }
      brandSvgFor(m[1], function (data) {
        if (!data || el.hasAttribute('data-ada-brand-keep')) { return; }
        var id = el.getAttribute('data-ada-pb');
        if (!id) { id = String(++pseudoSeq); el.setAttribute('data-ada-pb', id); }
        pseudoRules[id + which] = '[data-ada-pb="' + id + '"]' + PSEUDO_WEIGHT + which +
          '{background-image:' + bi.replace(m[1], data) + ' !important}';
        writePseudoRules();
      });
    });
  }
  /* Set an inline style once, remembering what was there so it can be put back. */
  function brandSet(node, prop, value) {
    node.__adaOrig = node.__adaOrig || {};
    if (!(prop in node.__adaOrig)) {
      node.__adaOrig[prop] = [node.style.getPropertyValue(prop), node.style.getPropertyPriority(prop)];
    }
    node.style.setProperty(prop, value, 'important');
  }
  function brandRestore(node) {
    var o = node.__adaOrig;
    if (o) {
      Object.keys(o).forEach(function (prop) {
        node.style.removeProperty(prop);
        if (o[prop][0]) { node.style.setProperty(prop, o[prop][0], o[prop][1]); }
      });
      node.__adaOrig = null;
    }
    var id = node.getAttribute('data-ada-pb');
    if (id) {
      delete pseudoRules[id + '::before'];
      delete pseudoRules[id + '::after'];
      node.removeAttribute('data-ada-pb');
      writePseudoRules();
    }
    node.removeAttribute('data-ada-brand');
    node.setAttribute('data-ada-brand-keep', '1');
  }
  /* A band with a photograph keeps its own blue, and so do the drip
     pictures directly above and below it. */
  function keepPhotoBand(el) {
    var waited = el.hasAttribute('data-ada-brand-wait');
    el.removeAttribute('data-ada-brand-wait');
    brandRestore(el);
    if (waited) { brandSet(el, 'background-color', BRAND_TARGET); el.__adaWaited = true; }
    [el.previousElementSibling, el.nextElementSibling].forEach(function (d) {
      if (!d) { return; }
      var dbi = (d.__adaOrig && d.__adaOrig['background-image']) ? 'svg' : window.getComputedStyle(d).backgroundImage;
      var loading = String(d.className).indexOf('pf-bg-lazy') >= 0;
      if (/\.svg|^svg$/i.test(dbi) || loading || d.hasAttribute('data-ada-pb')) { brandRestore(d); d.__adaBrandDone = true; }
    });
  }
  /* A drip next to a band whose picture is still loading waits for it, so
     it is never shown in one blue and then switched back to the other. */
  function besideLoadingBand(el) {
    return [el.previousElementSibling, el.nextElementSibling].some(function (d) {
      return !!d && String(d.className).indexOf('pf-bg-lazy') >= 0 &&
        (d.hasAttribute('data-ada-brand-wait') || brandRgb(window.getComputedStyle(d).backgroundColor));
    });
  }
  /* Recolour the old blue printed around a band picture. The picture is
     drawn onto a canvas, and only the backdrop is changed: the pixels that
     are the old blue, or a soft edge between the old blue and white, and
     that are joined to the picture's outer edge. Jars, labels, sprinkles
     and the white splash keep every pixel as they were. The result is kept
     for the visit, so each picture is worked out once. */
  var photoDone = {};
  function recolourPhoto(url, done) {
    var entry = photoDone[url];
    if (entry && entry.ready) { done(entry.data); return; }
    if (entry) { entry.waiting.push(done); return; }
    entry = photoDone[url] = { ready: false, data: '', waiting: [done] };
    function finish(data) {
      entry.ready = true; entry.data = data;
      entry.waiting.forEach(function (f) { f(data); });
      entry.waiting = [];
    }
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = function () { finish(''); };
    img.onload = function () {
      try {
        var scale = Math.min(1, 1600 / img.naturalWidth);
        var w = Math.round(img.naturalWidth * scale), h = Math.round(img.naturalHeight * scale);
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var cx = cv.getContext('2d');
        cx.drawImage(img, 0, 0, w, h);
        var id = cx.getImageData(0, 0, w, h), px = id.data;
        /* The backdrop colour is read from the picture's own border. */
        var sr = 0, sg = 0, sb = 0, sn = 0, total = 0;
        function sample(i) {
          total++;
          var r = px[i], g = px[i + 1], b = px[i + 2];
          if (Math.abs(r - 107) < 12 && Math.abs(g - 149) < 10 && b > 238) { sr += r; sg += g; sb += b; sn++; }
        }
        for (var x = 0; x < w; x += 4) { sample(x * 4); sample(((h - 1) * w + x) * 4); }
        for (var y = 0; y < h; y += 4) { sample(y * w * 4); sample((y * w + w - 1) * 4); }
        if (sn < total * 0.4) { finish(''); return; }
        var B = [sr / sn, sg / sn, sb / sn], T = [79, 109, 186];
        var D = [T[0] - B[0], T[1] - B[1], T[2] - B[2]];
        var WB = [255 - B[0], 255 - B[1], 255 - B[2]];
        var wb2 = WB[0] * WB[0] + WB[1] * WB[1] + WB[2] * WB[2];
        /* How much of the backdrop is in a pixel: 1 for the backdrop itself,
           falling to 0 across a soft edge towards white or any other colour. */
        var amt = new Float32Array(w * h);
        for (var k = 0, n = w * h; k < n; k++) {
          var o = k * 4;
          var dr = px[o] - B[0], dg = px[o + 1] - B[1], db = px[o + 2] - B[2];
          var d = Math.sqrt(dr * dr + dg * dg + db * db), a = 0;
          if (d <= 14) { a = 1; } else {
            var t = (dr * WB[0] + dg * WB[1] + db * WB[2]) / wb2;
            if (t > 0 && t < 1) {
              var er = dr - t * WB[0], eg = dg - t * WB[1], eb = db - t * WB[2];
              if (Math.sqrt(er * er + eg * eg + eb * eb) < 16) { a = 1 - t; }
            }
            if (!a && d < 60) { a = 1 - (d - 14) / 46; }
          }
          amt[k] = a;
        }
        /* Only backdrop joined to the outer edge is changed. */
        var seen = new Uint8Array(w * h), stack = [];
        function push(q) { if (!seen[q] && amt[q] > 0.02) { seen[q] = 1; stack.push(q); } }
        for (var ex = 0; ex < w; ex++) { push(ex); push((h - 1) * w + ex); }
        for (var ey = 0; ey < h; ey++) { push(ey * w); push(ey * w + w - 1); }
        while (stack.length) {
          var q = stack.pop(), qx = q % w;
          var f = amt[q], qo = q * 4;
          px[qo] = Math.round(px[qo] + D[0] * f);
          px[qo + 1] = Math.round(px[qo + 1] + D[1] * f);
          px[qo + 2] = Math.round(px[qo + 2] + D[2] * f);
          if (qx > 0) { push(q - 1); }
          if (qx < w - 1) { push(q + 1); }
          if (q >= w) { push(q - w); }
          if (q < (h - 1) * w) { push(q + w); }
        }
        cx.putImageData(id, 0, 0);
        cv.toBlob(function (blob) { finish(blob ? URL.createObjectURL(blob) : ''); }, 'image/jpeg', 0.92);
      } catch (e) { finish(''); }
    };
    /* A separate request keeps the browser from reusing a copy of the
       picture that was loaded without permission to be read back. */
    img.src = url + (url.indexOf('?') < 0 ? '?' : '&') + 'ada-cors=1';
  }
  /* Once its picture is ready, the band, the picture and the drips beside
     it all move to the corrected blue together. */
  function unifyPhotoBand(el, bi, url) {
    recolourPhoto(url, function (blobUrl) {
      if (!blobUrl) { if (el.__adaWaited) { brandRestore(el); } return; }
      var now = window.getComputedStyle(el).backgroundImage || '';
      if (now.indexOf(url) < 0) { return; }
      [el, el.previousElementSibling, el.nextElementSibling].forEach(function (n) {
        if (!n) { return; }
        n.removeAttribute('data-ada-brand-keep');
        if (n !== el) { n.__adaBrandDone = false; n.__adaPseudoDone = false; }
      });
      brandSet(el, 'background-image', now.replace(url, blobUrl));
      brandSet(el, 'background-color', BRAND_TARGET);
      el.setAttribute('data-ada-brand-photo', '1');
      unifyBrandBlue();
    });
  }
  /* Small pictures drawn as SVG files in the old blue, such as the social
     icons in the footer: the file is read once and shown with the old blue
     changed to the corrected blue. Every other colour in it stays. */
  function brandSvgImage(img) {
    var src = img.currentSrc || img.src || '';
    if (!src || src.indexOf('data:') === 0) { return false; }
    if (!/\.svg(\?|#|$)/i.test(src)) { return true; }
    brandSvgFor(src, function (data) {
      if (!data || (img.currentSrc || img.src) !== src) { return; }
      if (img.getAttribute('srcset')) { img.setAttribute('srcset', data); }
      img.src = data;
      img.__adaEdgeN = 6;
    });
    return true;
  }
  /* Thin borders and small icons drawn in the old blue, anywhere on the page.
     Apps load their own styles a little after the page, so an element is
     looked at on the first few passes, not only the first one. */
  function unifyBrandEdges() {
    var all = document.body ? document.body.querySelectorAll('*') : [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if ((el.__adaEdgeN || 0) >= 6) { continue; }
      if (/^(SCRIPT|STYLE|NOSCRIPT|TEMPLATE|VIDEO|SOURCE|IFRAME)$/.test(el.tagName)) { el.__adaEdgeN = 6; continue; }
      if (el.closest('[data-ada-brand-keep]')) { continue; }
      /* A picture that has not been given its file yet is looked at again
         once it has, however long that takes. */
      if (el.tagName === 'IMG') {
        if (brandSvgImage(el)) { el.__adaEdgeN = (el.__adaEdgeN || 0) + 1; }
        continue;
      }
      el.__adaEdgeN = (el.__adaEdgeN || 0) + 1;
      var cs = window.getComputedStyle(el);
      /* Icon fonts and any text still in the old blue. */
      if (brandRgb(cs.color)) { el.style.setProperty('color', BRAND_TARGET, 'important'); }
      if (window.SVGElement && el instanceof window.SVGElement) {
        if (brandRgb(cs.fill)) { el.style.setProperty('fill', BRAND_TARGET, 'important'); }
        if (brandRgb(cs.stroke)) { el.style.setProperty('stroke', BRAND_TARGET, 'important'); }
        continue;
      }
      /* Small solid shapes, such as the bars of the menu icon. */
      if (brandRgb(cs.backgroundColor) && (cs.backgroundImage || 'none') === 'none') {
        var box = el.getBoundingClientRect();
        if (box.width * box.height > 0 && box.width * box.height < 2500) {
          el.style.setProperty('background-color', BRAND_TARGET, 'important');
        }
      }
      ['top', 'right', 'bottom', 'left'].forEach(function (side) {
        if (parseFloat(cs.getPropertyValue('border-' + side + '-width')) > 0 &&
            brandRgb(cs.getPropertyValue('border-' + side + '-color'))) {
          el.style.setProperty('border-' + side + '-color', BRAND_TARGET, 'important');
        }
      });
    }
  }
  function unifyBrandBlue() {
    if (!pseudoSheet) { writePseudoRules(); }
    var roots = [document.getElementById('main-content'), document.querySelector('footer')];
    roots.forEach(function (root) {
      if (!root) { return; }
      var all = root.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        if ((el.__adaBrandDone && el.__adaPseudoDone) || el.hasAttribute('data-ada-brand-keep')) { continue; }
        if (/^(SCRIPT|STYLE|NOSCRIPT|TEMPLATE|IMG|VIDEO|PICTURE|SOURCE)$/.test(el.tagName) ||
            (window.SVGElement && el instanceof window.SVGElement)) {
          el.__adaBrandDone = el.__adaPseudoDone = true;
          continue;
        }
        brandPseudo(el);
        if (el.__adaBrandDone) { continue; }
        var cs = window.getComputedStyle(el);
        var bi = cs.backgroundImage || 'none';
        /* A background that has not loaded yet gets the corrected blue now,
           so the band never shows the old blue while it waits, and is looked
           at again once its picture arrives. */
        var lazy = el.className && String(el.className).indexOf('pf-bg-lazy') >= 0;
        if (lazy) {
          if (brandRgb(cs.backgroundColor)) {
            brandSet(el, 'background-color', BRAND_TARGET);
            el.setAttribute('data-ada-brand', '1');
            el.setAttribute('data-ada-brand-wait', '1');
          }
          continue;
        }
        /* An empty url() with nothing left to load, as the page builder leaves
           on phones, is no picture at all. */
        if (/^url\(\s*(["']\s*["']\s*)?\)$/.test(bi.trim())) { bi = 'none'; }
        /* A shape drawn as a gradient, such as the zigzag edge of a band,
           carries the brand blue inside the picture. Each stop in that blue
           takes the corrected blue, so the shape matches the band beside it,
           and the piece is not mistaken for a photograph. */
        if (bi !== 'none' && bi.indexOf('gradient') >= 0 && bi.indexOf('url(') < 0) {
          if (!el.hasAttribute('data-ada-brand-keep')) {
            var swapBg = bi.replace(/rgba?\([^)]*\)/g, function (colour) {
              return brandRgb(colour) ? BRAND_TARGET : colour;
            });
            if (swapBg !== bi) {
              brandSet(el, 'background-image', swapBg);
              el.setAttribute('data-ada-brand', '1');
            }
            if (brandRgb(cs.backgroundColor)) {
              brandSet(el, 'background-color', BRAND_TARGET);
              el.setAttribute('data-ada-brand', '1');
            }
          }
          el.__adaBrandDone = true;
          continue;
        }
        var m = bi.match(/url\(\s*["']?([^"')]+\.svg[^"')]*)["']?\s*\)/i);
        var photo = bi !== 'none' && !m && !/^url\(\s*["']?data:image\/svg/i.test(bi);
        var brand = brandRgb(cs.backgroundColor) ||
          !!(el.__adaOrig && el.__adaOrig['background-color'] && el.hasAttribute('data-ada-brand'));
        if (photo) {
          el.removeAttribute('data-ada-brand');
          el.__adaBrandDone = true;
          if (brand) {
            keepPhotoBand(el);
            var pm = bi.match(/url\(\s*["']?([^"')]+)["']?\s*\)/);
            if (pm) { unifyPhotoBand(el, bi, pm[1]); }
          }
          continue;
        }
        el.removeAttribute('data-ada-brand-wait');
        if (brandRgb(cs.backgroundColor)) {
          brandSet(el, 'background-color', BRAND_TARGET);
          el.setAttribute('data-ada-brand', '1');
        }
        /* The page builder applies its backgrounds after first paint, so an
           element of its that has no picture yet is looked at again later. */
        if (!m && (!bi || bi === 'none') && el.closest('.__pf')) { continue; }
        if (m && besideLoadingBand(el)) { continue; }
        el.__adaBrandDone = true;
        if (!m) { continue; }
        (function (node, url, full) {
          brandSvgFor(url, function (data) {
            if (!data || node.hasAttribute('data-ada-brand-keep')) { return; }
            brandSet(node, 'background-image', full.replace(url, data));
            node.setAttribute('data-ada-brand', '1');
          });
        })(el, m[1], bi);
      }
    });
  }

  /* =========================================================================
     19b. Product option swatches - WCAG 2.1.1 Keyboard, 4.1.2 Name Role Value
          The tiles a shopper sees become the radio group itself: each tile is
          a stop of its own, so Tab walks the options one by one, the arrow
          keys move along them as well, and Enter or Space picks the one the
          keyboard is on. The theme's own inputs still carry the choice into
          the form; they are simply no longer the thing the keyboard lands on,
          because a browser only ever offers one of them as a tab stop.
          Nothing changes for a mouse user.
     ====================================================================== */
  function swatchTiles(group) {
    return [].slice.call(group.querySelectorAll('.swatch-element'));
  }
  function swatchInput(tile) {
    return tile.querySelector('input[type="radio"]') ||
      (tile.previousElementSibling && tile.previousElementSibling.matches &&
       tile.previousElementSibling.matches('input[type="radio"]') ? tile.previousElementSibling : null);
  }
  function syncSwatchTiles(group) {
    swatchTiles(group).forEach(function (tile) {
      var input = swatchInput(tile);
      if (!input) { return; }
      setAttr(tile, 'aria-checked', input.checked ? 'true' : 'false');
      setAttr(tile, 'tabindex', '0');
      if (input.disabled || tile.className.indexOf('soldout') >= 0) {
        setAttr(tile, 'aria-disabled', 'true');
      } else {
        tile.removeAttribute('aria-disabled');
      }
    });
  }
  function selectSwatch(tile) {
    var input = swatchInput(tile);
    if (!input || input.checked) { return; }
    input.checked = true;
    try { input.click(); } catch (cannotClick) { /* ignore */ }
    try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (noEvent) { /* ignore */ }
    var label = tile.querySelector('label');
    if (label) { try { label.click(); } catch (cannotClickLabel) { /* ignore */ } }
    /* Clicking the input or its label pulls focus onto the hidden input, so
       the keyboard is put back on the tile, which is what the shopper sees
       and what carries the focus ring. */
    var restore = function () {
      if (document.activeElement !== tile && tile.contains && !tile.contains(document.activeElement)) { return tile.focus(); }
      if (document.activeElement && document.activeElement.type === 'radio') { tile.focus(); }
    };
    try { restore(); } catch (cannotFocus) { /* ignore */ }
    window.setTimeout(function () { try { restore(); } catch (err) { /* ignore */ } }, 60);
  }
  function wireProductSwatches() {
    each('.swatch', function (group) {
      var tiles = swatchTiles(group);
      if (!tiles.length) { return; }
      setAttr(group, 'role', 'radiogroup');
      if (!group.getAttribute('aria-label')) {
        var title = group.querySelector('.option_title, .header');
        var name = title ? textOf(title).replace(/:\s*$/, '') : '';
        if (name) { setAttr(group, 'aria-label', name); }
      }
      tiles.forEach(function (tile) {
        var input = swatchInput(tile);
        if (!input) { return; }
        /* The native input keeps the value for the form and for the theme's
           own script, and steps out of the keyboard order and the reading
           order, so the tile is the single place the choice is made. */
        setAttr(input, 'tabindex', '-1');
        setAttr(input, 'aria-hidden', 'true');
        setAttr(tile, 'role', 'radio');
        if (!tile.getAttribute('aria-label')) {
          var lab = tile.querySelector('label');
          var text = lab ? textOf(lab) : textOf(tile);
          if (text) { setAttr(tile, 'aria-label', text); }
        }
        if (tile.getAttribute('data-ada-sw') === '1') { return; }
        tile.setAttribute('data-ada-sw', '1');
        tile.addEventListener('keydown', function (e) {
          var key = e.key;
          var list = swatchTiles(group).filter(function (t) { return t.getAttribute('aria-disabled') !== 'true'; });
          var here = list.indexOf(tile);
          if (key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 'Space') {
            e.preventDefault();
            selectSwatch(tile);
            syncSwatchTiles(group);
            return;
          }
          var step = 0;
          if (key === 'ArrowRight' || key === 'Right' || key === 'ArrowDown' || key === 'Down') { step = 1; }
          if (key === 'ArrowLeft' || key === 'Left' || key === 'ArrowUp' || key === 'Up') { step = -1; }
          if (!step || here < 0) { return; }
          e.preventDefault();
          var next = list[(here + step + list.length) % list.length];
          if (!next) { return; }
          try { next.focus(); } catch (cannotFocus) { /* ignore */ }
          selectSwatch(next);
          syncSwatchTiles(group);
        });
        tile.addEventListener('click', function () { window.setTimeout(function () { syncSwatchTiles(group); }, 60); });
        input.addEventListener('change', function () { syncSwatchTiles(group); });
      });
      syncSwatchTiles(group);
      if (group.getAttribute('data-ada-swg') !== '1') {
        group.setAttribute('data-ada-swg', '1');
        try {
          new MutationObserver(debounce(function () { syncSwatchTiles(group); }, 80))
            .observe(group, { attributes: true, subtree: true, attributeFilter: ['class', 'checked'] });
        } catch (noObserver) { /* ignore */ }
      }
    });
  }

  /* A colour fade that begins inside a section the browser has not drawn yet
     never advances, and while it is stopped it holds the element at a value
     part way between the old colour and the new one, above even a rule marked
     important. Any fade found stopped at its first frame is moved to its end,
     so the element paints the colour it is meant to have. */
  function releaseStalledFades(depth) {
    if (!document.getAnimations) { return; }
    var list = document.getAnimations();
    var moved = 0;
    for (var i = 0; i < list.length; i++) {
      var a = list[i];
      if (!a.transitionProperty || a.playState !== 'running' || a.currentTime) { continue; }
      moved++;
      try { a.finish(); } catch (e) { try { a.cancel(); } catch (e2) { /* ignore */ } }
    }
    /* Settling one fade can start the same fade on the piece of text inside
       it, which stops in the same way, so the pass is repeated a few times
       until nothing is left waiting. */
    var d = depth || 0;
    if (moved && d < 5) {
      window.setTimeout(function () { try { releaseStalledFades(d + 1); } catch (e) { /* ignore */ } }, 150);
    }
  }
  function runAll() {
    var jobs = [labelCartLinks, wireProductSwatches, releaseStuckOverlays, releaseStalledFades, upgradeToggleHeadings, wireDropdowns, wireAppMegaMenu, wireDialogs, announceCart,
      unifyBrandBlue, unifyBrandEdges, fixRepublishedPageBuilderColours, wireMegaMenuTabs, labelPageBuilderFields, dedupeGeneratedIds,
      unheadLinkOnlyHeadings, dedupeSvgIds, dedupeDocumentIds, nameEmbeddedFrames, fixLandmarks, fixBlogListing,
      fixReviewsWidgets, fixPageBuilderProductBoxes, fixProductCards, fixWishlist, fixFlickity, fixProductBuilder,
      fixImageAlts, fixAppMarkup, normaliseHeadingLevels, labelPlaceholderOnlyFields, hideEmptyDecorativeLinks,
      nameEmptyControls, hideDuplicateDrawers, watchDrawer];
    for (var i = 0; i < jobs.length; i++) {
      try { jobs[i](); } catch (e) { /* one failing job must not stop the rest */ }
    }
  }

  ready(function () {
    watchCartWrites();
    runAll();
    /* apps clone parts of the header after first paint, so run the pass again later */
    window.setTimeout(runAll, 300);
    window.setTimeout(runAll, 900);
    window.setTimeout(runAll, 2000);
    window.setTimeout(runAll, 4000);
    window.setTimeout(runAll, 8000);
    window.setTimeout(runAll, 12000);
    var adaTicks = 0;
    var adaTidy = window.setInterval(function () {
      adaTicks++;
      try { dedupeGeneratedIds(); } catch (e) { /* ignore */ }
      try { dedupeSvgIds(); } catch (e) { /* ignore */ }
      try { dedupeDocumentIds(); } catch (e) { /* ignore */ }
      if (adaTicks > 6) { window.clearInterval(adaTidy); }
    }, 2500);

    /* The mega menu builds itself the first time it is opened. Rewiring on the
       Tab keypress itself keeps the whole menu reachable from the first open. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) { return; }
      var t = e.target;
      if (!t || !t.closest) { return; }
      if (!t.closest('.main_nav, .tmenu_menu, .dropdown_container, .mobile_nav')) { return; }
      try { wireMegaMenuTabs(); } catch (err) { /* ignore */ }
    }, true);
    var adaLastWire = 0;
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || !t.closest) { return; }
      if (!t.closest('.main_nav, .tmenu_menu, .dropdown_container, .mobile_nav')) { return; }
      var now = Date.now();
      if (now - adaLastWire < 150) { return; }
      adaLastWire = now;
      try { wireMegaMenuTabs(); } catch (err) { /* ignore */ }
    }, true);
    document.addEventListener('keydown', activateOnKey, false);
    document.addEventListener('keydown', noteTab, true);
    document.addEventListener('pointerdown', notePointer, true);
    document.addEventListener('mousedown', notePointer, true);
    document.addEventListener('touchstart', notePointer, { capture: true, passive: true });
    document.addEventListener('focusin', rememberHeaderFocus, true);
    document.addEventListener('focusout', function () { window.setTimeout(restoreLostHeaderFocus, 0); }, true);
    document.addEventListener('keydown', letTabOutOfMegaMenu, true);
    document.addEventListener('keydown', escapeClosesDropdowns, true);
    document.addEventListener('focusout', closeDropdownWhenFocusLeaves, true);
    document.addEventListener('keydown', dialogKeys, true);

    var soft = debounce(runAll, 250);
    try {
      new MutationObserver(soft).observe(document.body, { childList: true, subtree: true, characterData: true });
    } catch (e) { /* ignore */ }
    /* The gallery script writes aria-selected back onto its slides every time
       the carousel moves; the reviews app re-renders its badges. */
    try {
      new MutationObserver(function (muts) {
        var again = false;
        for (var i = 0; i < muts.length; i++) {
          var m = muts[i].target;
          if (!m || !m.getAttribute) { continue; }
          if (muts[i].attributeName === 'aria-selected' && m.getAttribute('aria-selected') !== null && m.closest('.flickity-slider, .gallery-cell')) { m.removeAttribute('aria-selected'); }
          if (m.classList && (m.classList.contains('jdgm-prev-badge__stars') || m.classList.contains('jdgm-carousel-item__review-rating') || m.classList.contains('cpb-render-konva'))) {
            if (m.getAttribute('tabindex') === '0' || m.getAttribute('tabindex') === '1' || m.getAttribute('role') === 'button') { again = true; }
          }
        }
        if (again) { soft(); }
      }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-selected', 'role', 'tabindex'] });
    } catch (e) { /* ignore */ }
    window.addEventListener('resize', debounce(function () { try { fixReviewsWidgets(); } catch (e) { /* ignore */ } }, 200));
    /* a drawer that slides shut changes nothing in the DOM, so re-check the
       dialogs whenever a transition finishes and whenever the viewport resizes */
    var adaRecheck = debounce(recheckDialogs, 60);
    document.addEventListener('transitionend', adaRecheck, true);
    document.addEventListener('animationend', adaRecheck, true);
    window.addEventListener('resize', adaRecheck);
    /* The first touch of a finger is also a moment to make sure nothing is
       still holding the page still: the check is made before the finger
       moves, so the very first swipe after a drawer closes already works. */
    document.addEventListener('touchstart', function () {
      try {
        if (openDialog() || coveringPanelOpen()) { fingerDown = true; return; }
        holdPageTouch(false);
        fingerDown = true;
      } catch (touchCheckFailed) { /* ignore */ }
    }, { capture: true, passive: true });
    document.addEventListener('touchend', function () { fingerDown = false; adaRecheck(); }, { capture: true, passive: true });
    document.addEventListener('touchcancel', function () { fingerDown = false; }, { capture: true, passive: true });

    document.addEventListener('cart:updated', soft);
    document.addEventListener('cart:refresh', soft);
    if (window.jQuery) {
      window.jQuery(document).on('cart.requestComplete ajaxCart.afterCartLoad cart:updated', soft);
      window.jQuery(document).ajaxComplete(soft);
    }
    try {
      new MutationObserver(soft).observe(document.head, { childList: true, subtree: true });
    } catch (e) { /* ignore */ }
    window.addEventListener('scroll', debounce(function () { try { releaseStalledFades(); unifyBrandBlue(); unifyBrandEdges(); fixRepublishedPageBuilderColours(); } catch (e) { /* ignore */ } }, 250), { passive: true });
    /* Pictures that load late, such as the footer icons, are recoloured as they arrive. */
    document.addEventListener('load', debounce(function (e) { try { unifyBrandEdges(); } catch (err) { /* ignore */ } }, 150), true);
    window.addEventListener('load', function () {
      window.setTimeout(soft, 400);
      window.setTimeout(soft, 1500);
      window.setTimeout(soft, 4000);
      /* Embeds from other companies, such as the short video on a product
         page, arrive well after the page has settled and are measured only
         once they have drawn themselves, so the page is looked at twice more
         while it is still new. */
      window.setTimeout(soft, 9000);
      window.setTimeout(soft, 15000);
    });
  });
})();

/* ADA fix - WCAG 1.3.1 Info and Relationships and 4.1.2 Name Role Value.
Three refinements that sit alongside the fixes above. None of them
touches the theme markup, the menu behaviour, or anything a sighted
visitor can see.
1. The mobile mega menu is built with wrapper divs sitting inside the
list, so the list items are not owned by the list and the menu groups
are announced wrongly. Roles restore the relationship in place, and any
role already set elsewhere is left as it is.
2. The wishlist app leaves an empty template link in the page with no
product behind it and no name. It is kept out of the reading order.
3. A late repeat of the heading rule above. The footer headings are
drawn by the page builder after the first pass has already run, which
leaves one jump in the order. This uses the same rule and only ever
closes a jump, so a level set on purpose is never raised or removed. */
(function () {
function fixMegaMenuLists() {
document.querySelectorAll('ul.mobile-mega-menu').forEach(function (ul) {
if (!ul.getAttribute('role')) { ul.setAttribute('role', 'list'); }
Array.prototype.forEach.call(ul.children, function (child) {
if (child.tagName !== 'LI' && !child.getAttribute('role')) { child.setAttribute('role', 'none'); }
});
ul.querySelectorAll('li').forEach(function (li) {
if (!li.getAttribute('role')) { li.setAttribute('role', 'listitem'); }
});
});
}
function fixWishlistStubs() {
document.querySelectorAll('.flits-wls-button').forEach(function (a) {
if ((a.textContent || '').trim()) { return; }
if (a.getAttribute('data-flits-product-id')) {
if (a.getAttribute('aria-hidden') === 'true') { a.removeAttribute('aria-hidden'); }
if (!a.getAttribute('aria-label') && !a.getAttribute('title')) { a.setAttribute('aria-label', 'Add to wishlist'); }
return;
}
if (a.getAttribute('aria-label') || a.getAttribute('title')) { return; }
a.setAttribute('aria-hidden', 'true');
});
}
function closeHeadingSkips() {
var prev = 0;
document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function (h) {
var role = h.getAttribute('role');
if (role === 'presentation' || role === 'none') { return; }
var r = h.getBoundingClientRect();
if (r.width < 1 || r.height < 1) { return; }
var lvl = parseInt(h.getAttribute('aria-level'), 10) || parseInt(h.tagName.slice(1), 10);
if (prev && lvl > prev + 1) { lvl = prev + 1; h.setAttribute('aria-level', String(lvl)); }
prev = lvl;
});
}
function run() {
try { fixMegaMenuLists(); } catch (e) { /* ignore */ }
try { fixWishlistStubs(); } catch (e) { /* ignore */ }
try { closeHeadingSkips(); } catch (e) { /* ignore */ }
}
if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', run); }
else { run(); }
window.addEventListener('load', run);
window.setTimeout(run, 1200);
window.setTimeout(run, 3000);
window.setTimeout(run, 6000);
})();
